import React, { useState, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TouchableWithoutFeedback, Linking, Image, Dimensions, ActivityIndicator, Platform, Share, ImageStyle, Animated } from 'react-native';
import { InsetModal as Modal } from './InsetModal';
import { colors, spacing, typography, chat } from '../theme';
import { Ionicons } from '@expo/vector-icons';
import { Video, ResizeMode } from 'expo-av';
import * as Clipboard from 'expo-clipboard';
import * as FileSystem from 'expo-file-system/legacy';
import { getCachedDocument, setCachedDocument } from '../utils/documentDownloadCache';
import { getApiBaseURL } from '../utils/imageProxy';
import { useAppStore } from '../store/appStore';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { createTranslator } from '../i18n';
import { useThemedStyles } from '../hooks/useThemedStyles';

// WebView is not supported on web; we use an iframe for PDF on web instead
const WebView = Platform.OS !== 'web' ? require('react-native-webview').WebView : null;
import { ProductOrderCard, ProductLinkCard, CatalogLinkCard, OrderMessageCard, CalendarEventCard, DataSetReplyCard, OutboundOptionsCard, OutboundOptionsList, VoiceNotePlayer } from './conversation-cards';
import type { OrderMessageAction } from './conversation-cards';
import { withAlpha } from '../theme';

const { width: screenWidth } = Dimensions.get('window');
const isMobileBrowser = Platform.OS === 'web' && (screenWidth < 768);
const isNativeMobile = Platform.OS !== 'web';
const messageLaneMaxWidth = '70%';
const numericMessageLaneMaxWidth = screenWidth * 0.7;
const mediaPreviewMaxWidth = Math.min(numericMessageLaneMaxWidth, 420);
const mediaPreviewHeight = Math.min(screenWidth * 0.5, 260);
// Order/special cards share the same lane width as regular message bubbles.
const specialCardMinWidth = isNativeMobile ? 0 : 280;
const bubbleRadius = 20;
const bubbleTailRadius = 6;

const looksLikePhoneNumber = (value?: string | null) => {
  if (!value || typeof value !== 'string') {
    return false;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  return /^[+]?[-()\s\d]{6,}$/.test(trimmed);
};

type DocumentTypeKind = 'pdf' | 'word' | 'excel' | 'powerpoint' | 'text' | 'image' | 'generic';
const getDocumentType = (mediaType?: string | null, filename?: string | null): { kind: DocumentTypeKind; label: string; icon: string; canPreviewInline: boolean } => {
  const mime = (mediaType || '').toLowerCase();
  const name = (filename || '').toLowerCase();
  if (mime.includes('pdf') || name.endsWith('.pdf')) return { kind: 'pdf', label: 'PDF', icon: 'document-text', canPreviewInline: true };
  if (mime.includes('msword') || mime.includes('word') || /\.docx?$/.test(name)) return { kind: 'word', label: 'Word', icon: 'document', canPreviewInline: false };
  if (mime.includes('spreadsheet') || mime.includes('excel') || /\.xlsx?$/.test(name)) return { kind: 'excel', label: 'Excel', icon: 'grid', canPreviewInline: false };
  if (mime.includes('presentation') || mime.includes('powerpoint') || /\.pptx?$/.test(name)) return { kind: 'powerpoint', label: 'PowerPoint', icon: 'easel', canPreviewInline: false };
  if (mime.includes('text/plain') || name.endsWith('.txt') || name.endsWith('.csv')) return { kind: 'text', label: 'Text', icon: 'document-text-outline', canPreviewInline: false };
  if (mime.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|bmp)$/.test(name)) return { kind: 'image', label: 'Image', icon: 'image', canPreviewInline: true };
  return { kind: 'generic', label: 'Document', icon: 'document-attach', canPreviewInline: false };
};

const formatFileSize = (bytes?: number | null): string => {
  if (bytes == null || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Prefer message.mediaName (origin file name), then try to extract from mediaUrl (proxy or Firebase), then content, then fallback. */
const getDocumentDisplayName = (
  mediaName?: string | null,
  mediaUrl?: string | null,
  content?: string | null
): string => {
  const trimmed = (s: string) => (s || '').trim();
  if (mediaName && trimmed(mediaName)) return trimmed(mediaName);
  if (mediaUrl && typeof mediaUrl === 'string') {
    try {
      const u = new URL(mediaUrl);
      // Proxy format: .../proxy?url=ENCODED_FIREBASE_URL&mimeType=...
      const inner = u.searchParams.get('url');
      const urlToUse = inner && (inner.startsWith('http') ? inner : null) ? inner : mediaUrl;
      const pathUrl = new URL(urlToUse);
      const segs = pathUrl.pathname.split('/').filter(Boolean);
      let last = segs[segs.length - 1];
      if (!last) return 'Document';
      last = decodeURIComponent(last);
      // Firebase path can be one segment with %2F-encoded slashes (e.g. .../o/foo%2Fbar%2Ffile.ogg)
      const afterSlash = last.includes('/') ? last.slice(last.lastIndexOf('/') + 1) : last;
      if (afterSlash && /\.\w{2,}$/.test(afterSlash)) return afterSlash;
    } catch (_) {}
  }
  if (content && /\.\w{2,}$/.test(content)) return trimmed(content);
  return 'Document';
};

/** Save document in-app only: web = blob download to disk; native = save to app storage and optional callback. Never opens URL externally. */
const downloadDocument = (
  url: string,
  filename: string,
  options?: { authToken?: string; onSaved?: (localUri: string) => void }
) => {
  if (Platform.OS === 'web' && typeof document !== 'undefined') {
    fetch(url, { credentials: 'omit', mode: 'cors' })
      .then((res) => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.blob(); })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename || 'document';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => { /* keep in-app: do not open URL externally */ });
  } else {
    const dir = `${FileSystem.documentDirectory}downloads`;
    const ext = filename.includes('.') ? filename.slice(filename.lastIndexOf('.')) : '';
    const safeName = `doc_${Date.now()}${ext}`;
    const localPath = `${dir}/${safeName}`;
    const downloadOpts = options?.authToken ? { headers: { Authorization: `Bearer ${options.authToken}` } } : undefined;
    FileSystem.getInfoAsync(dir).then((info) => {
      if (!info.exists) return FileSystem.makeDirectoryAsync(dir, { intermediates: true });
    }).then(() =>
      FileSystem.downloadAsync(url, localPath, downloadOpts)
    ).then(({ uri }) => {
      options?.onSaved?.(uri);
    }).catch(() => { /* keep in-app: do not open URL externally */ });
  }
};

/** Renders a PDF on web by fetching the file and showing it in an iframe via a blob URL.
 * This avoids loading the API URL in the iframe, so X-Frame-Options from the API/proxy never applies. */
const PdfIframeWeb = ({ uri, authToken, style }: { uri: string; authToken?: string | null; style?: any }) => {
  const styles = useThemedStyles(getStyles);
  const { locale } = useLocale();
  const t = createTranslator(locale);
  const containerRef = useRef<View | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (Platform.OS !== 'web' || !uri || uri.trim() === '') return;
    const u = uri.trim();
    if (u.endsWith('/') && !u.includes('?')) {
      setError(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    const headers: Record<string, string> = {};
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    fetch(u, { credentials: 'omit', mode: 'cors', headers })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setBlobUrl(url);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
      const prev = blobUrlRef.current;
      if (prev) {
        try { URL.revokeObjectURL(prev); } catch (_) {}
        blobUrlRef.current = null;
      }
      setBlobUrl(null);
    };
  }, [uri, retryKey, authToken]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !blobUrl) return;
    const el = (containerRef.current as any) as HTMLElement | null;
    if (!el) return;
    const iframe = document.createElement('iframe');
    iframe.src = blobUrl;
    iframe.title = 'PDF viewer';
    iframe.setAttribute('style', 'width:100%;height:100%;min-height:400px;border:none;');
    el.appendChild(iframe);
    return () => {
      try { el.removeChild(iframe); } catch (_) {}
    };
  }, [blobUrl]);

  if (error) {
    return (
      <View style={[styles.documentViewerFallback, style]}>
        <Ionicons name="document-text" size={48} color={colors.primary} />
        <Text style={styles.documentViewerFallbackLabel}>{t('chat.pdfLoadError')}</Text>
        <TouchableOpacity
          style={styles.documentViewerOpenButton}
          onPress={() => setRetryKey((k) => k + 1)}
        >
          <Ionicons name="refresh-outline" size={20} color={colors.white} />
          <Text style={styles.documentViewerOpenButtonText}>{t('chat.pdfRetry')}</Text>
        </TouchableOpacity>
      </View>
    );
  }
  if (loading) {
    return (
      <View style={[styles.documentViewerWrapper, styles.documentViewerLoading, style]}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={styles.documentViewerLoadingText}>{t('chat.pdfLoading')}</Text>
      </View>
    );
  }
  return <View ref={containerRef} style={[styles.documentWebView, style]} />;
};

/** Renders Google Docs viewer iframe on web for Office/generic docs. In-app only. */
const OfficeViewerIframeWeb = ({ uri, style }: { uri: string; style?: any }) => {
  const styles = useThemedStyles(getStyles);
  const containerRef = useRef<View | null>(null);
  useEffect(() => {
    if (Platform.OS !== 'web' || !uri || !containerRef.current) return;
    const el = (containerRef.current as any) as HTMLElement | null;
    if (!el) return;
    const iframe = document.createElement('iframe');
    iframe.src = uri;
    iframe.title = 'Document viewer';
    iframe.setAttribute('style', 'width:100%;height:100%;min-height:400px;border:none;');
    el.appendChild(iframe);
    return () => {
      try { el.removeChild(iframe); } catch (_) {}
    };
  }, [uri]);
  return <View ref={containerRef} style={[styles.documentWebView, style]} />;
};

/** Web: in-app only fallback when embedded viewer isn't available (no external view/download). */
const OfficeViewerWebFallback = ({ filename, style }: { filename?: string; style?: any }) => {
  const styles = useThemedStyles(getStyles);
  const { locale } = useLocale();
  const t = createTranslator(locale);
  return (
    <View style={[styles.documentViewerFallback, style]}>
      <Ionicons name="document-text-outline" size={56} color={colors.primary} />
      <Text style={styles.documentViewerFallbackLabel}>{t('chat.previewUnavailable')}</Text>
      {filename ? (
        <Text style={styles.documentViewerFallbackName} numberOfLines={2}>{filename}</Text>
      ) : null}
      <Text style={styles.documentViewerFallbackHint}>
        {t('chat.previewUnavailableHint')}
      </Text>
    </View>
  );
};

const resolveContactLabel = (fallback?: string | null, ...candidates: Array<string | undefined | null>) => {
  for (const candidate of candidates) {
    if (!candidate) {
      continue;
    }
    const trimmed = candidate.trim();
    if (!trimmed || trimmed.toLowerCase() === 'unknown contact') {
      continue;
    }
    if (looksLikePhoneNumber(trimmed)) {
      continue;
    }
    if (fallback && !looksLikePhoneNumber(fallback)) {
      return fallback;
    }
    return trimmed;
  }
  return undefined;
};

/** Extract display text from message content (string or JSON/object-like string). */
const getDisplayContent = (content: any): string => {
  if (content == null) return '';
  if (typeof content === 'string') {
    const trimmed = content.trim();
    if (trimmed.startsWith('{')) {
      try {
        const obj = JSON.parse(trimmed);
        const extracted = extractTextFromContentObject(obj);
        if (extracted) return extracted;
      } catch (_) {}
      // Fallback for object-like strings (e.g. {text:"..."} or {"text":"..."})
      const textMatch = trimmed.match(/(?:^|[,{])\s*"?text"?\s*:\s*"((?:[^"\\]|\\.)*)"|(?:^|[,{])\s*"?text"?\s*:\s*'([^']*)'/);
      if (textMatch) {
        const v = (textMatch[1] ?? textMatch[2] ?? '').trim();
        if (v) return v;
      }
      const bodyMatch = trimmed.match(/(?:^|[,{])\s*"?body"?\s*:\s*"((?:[^"\\]|\\.)*)"|(?:^|[,{])\s*"?body"?\s*:\s*'([^']*)'/);
      if (bodyMatch) {
        const v = (bodyMatch[1] ?? bodyMatch[2] ?? '').trim();
        if (v) return v;
      }
    }
    return trimmed;
  }
  if (typeof content === 'object') return extractTextFromContentObject(content) || '';
  return String(content);
};

const extractTextFromContentObject = (obj: any): string => {
  if (!obj || typeof obj !== 'object') return '';
  const body = obj.body;
  if (typeof body === 'string' && body.trim()) return body.trim();
  if (body && typeof body.text === 'string' && body.text.trim()) return body.text.trim();
  if (typeof obj.text === 'string' && obj.text.trim()) return obj.text.trim();
  if (typeof obj.title === 'string' && obj.title.trim()) return obj.title.trim();
  if (typeof obj.content === 'string' && obj.content.trim()) return obj.content.trim();
  return '';
};

type MessageLocationPayload = {
  latitude: number;
  longitude: number;
  address?: string;
  text?: string;
  city?: string;
  country?: string;
};

interface Message {
  _id: string;
  messageId: string;
  externalId?: string;
  contactId: string;
  from: string;
  to: string;
  type: 'text' | 'image' | 'video' | 'audio' | 'document' | 'location' | 'contact' | 'sticker' | 'template' | 'interactive' | 'calendar';
  originalType?: 'text' | 'image' | 'video' | 'audio' | 'document' | 'location' | 'contact' | 'sticker' | 'template' | 'interactive' | 'button' | 'calendar';
  content: string;
  mediaUrl?: string;
  mediaType?: string;
  mediaName?: string;
  mediaSize?: number;
  latitude?: number;
  longitude?: number;
  address?: string;
  text?: string;
  city?: string;
  country?: string;
  timestamp: string;
  direction: 'inbound' | 'outbound' | 'outbound-api';
  status: 'sent' | 'delivered' | 'read' | 'failed' | 'pending';
  statusTimestamp?: string;
  // Only meaningful when status === 'failed' — Meta's delivery-status webhook error, e.g.
  // "This message was not delivered to maintain healthy ecosystem engagement." (code 131049).
  errorMessage?: string;
  errorCode?: number;
  providerId: string;
  phoneNumberId: string;
  tags: string[];
  category?: string;
  priority?: 'low' | 'normal' | 'high';
  createdAt: string;
  updatedAt: string;
  // Reply message support
  replyTo?: string;
  replyToMessage?: Message;
  interactiveType?: string;
  // Contact message specific fields
  contactName?: string;
  contactPhone?: string;
  interactive?: any;
  // Quick-reply rich cards (product, calendar, data_set)
  interactiveData?: {
    buttonId?: string;
    itemType?: 'product' | 'calendar' | 'data_set';
    orderId?: string;
    calendarEventId?: string;
    quickReplyItemId?: string;
    [key: string]: any;
  };
  referenceType?: 'product' | 'catalog' | 'calendar' | 'order' | 'quotation';
  referenceId?: string;
  externalReferences?: {
    orderId?: string;
    calendarEventId?: string;
    [key: string]: any;
  };
}

interface MessageBubbleProps {
  message: Message;
  isLastInGroup?: boolean;
  showTimestamp?: boolean;
  onReply?: (message: Message) => void;
  onForward?: (message: Message) => void;
  onCopy?: (message: Message) => void;
  onShare?: (message: Message) => void;
  onScrollToMessage?: (messageId: string) => void;
  isHighlighted?: boolean;
  fallbackContactName?: string;
  businessName?: string;
  /** Current conversation ID (e.g. for order card to notify contact on status change) */
  conversationId?: string;
  /** Maximum bubble width, already calculated as 70% of the actual conversation pane. */
  maxBubbleWidth?: number;
  onRetry?: (message: any) => void | Promise<void>;
  onSetContactLocation?: (message: any, location: MessageLocationPayload) => void | Promise<void>;
  onCreateOrderAtLocation?: (message: any, location: MessageLocationPayload) => void | Promise<void>;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isLastInGroup = false,
  showTimestamp = false,
  onReply,
  onForward,
  onCopy,
  onShare,
  onScrollToMessage,
  isHighlighted = false,
  fallbackContactName,
  businessName,
  conversationId,
  maxBubbleWidth,
  onRetry,
  onSetContactLocation,
  onCreateOrderAtLocation,
}) => {
  const styles = useThemedStyles(getStyles);
  const { isRTL, locale } = useLocale();
  const t = createTranslator(locale);
  const { showNotification, navigateTo } = useAppContext();
  // On web, CSS dir="rtl" already mirrors flex layouts — don't double-flip with row-reverse.
  const needsFlip = isRTL && Platform.OS !== 'web';
  const isOutgoing = message.direction === 'outbound' || message.direction === 'outbound-api';
  const isIncoming = message.direction === 'inbound';
  const messageType = React.useMemo(() => (message.type || '').toLowerCase(), [message.type]);
  const [isLongTextExpanded, setIsLongTextExpanded] = useState(false);

  useEffect(() => {
    setIsLongTextExpanded(false);
  }, [message.messageId, message._id]);

  const trimmedTextContent = React.useMemo(() => {
    if (messageType !== 'text') return '';
    return (message.content || '').trim();
  }, [message, messageType]);

  const isLinkOnlyMessage = React.useMemo(() => {
    if (message.type !== 'text') return false;
    const text = trimmedTextContent;
    if (!text) return false;
    if (/\s/.test(text)) return false;
    const lower = text.toLowerCase();
    return (
      lower.startsWith('http://') ||
      lower.startsWith('https://') ||
      lower.startsWith('www.') ||
      lower.startsWith('ftp://') ||
      lower.startsWith('mailto:')
    );
  }, [message, trimmedTextContent]);

  /** Plain CTA URL (no product/catalog): action shown below card like OutboundOptionsList */
  const ctaUrlActionMeta = React.useMemo((): { displayText: string; url: string } | null => {
    const parsed = message.interactiveData || message.interactive;
    const isCtaUrl = parsed?.type === 'cta_url' || message.interactiveType === 'cta_url';
    const url = parsed?.action?.parameters?.url || parsed?.action?.url;
    if (!isCtaUrl || typeof url !== 'string') return null;
    try {
      const urlObj = new URL(url);
      const params = Object.fromEntries(urlObj.searchParams.entries());
      if (params.productId || params.product_id || params.catalogId || params.catalog_id) return null;
    } catch (_) {}
    const displayText = parsed?.action?.parameters?.display_text || parsed?.action?.display_text || t('chat.openLink');
    return { displayText, url };
  }, [message]);

  const outboundOptionsMeta = React.useMemo((): {
    isOutbound: boolean;
    bodyText: string;
    headerText: string;
    bodyOnly: string;
    footerText: string;
    optionTitles: string[];
    /** Fixed message with options: optional header media (image/video/document) from interactive header */
    headerMedia?: { type: 'image' | 'video' | 'document'; url: string; filename?: string };
  } | null => {
    const isOut = message.direction === 'outbound' || message.direction === 'outbound-api';
    if (!isOut || (message.type !== 'interactive' && message.originalType !== 'interactive')) return null;
    const parsed = message.interactiveData || message.interactive;
    const buttons = parsed?.action?.buttons || parsed?.buttons;
    const sections = parsed?.action?.sections;
    const fromButtons = Array.isArray(buttons)
      ? buttons.map((b: any) => b.reply?.title || b.title || (typeof b === 'string' ? b : '')).filter((t: string) => t && t.trim())
      : [];
    const fromList = Array.isArray(sections)
      ? sections.flatMap((s: any) => (s.rows || []).map((r: any) => r.title || r.description || '')).filter((t: string) => t && t.trim())
      : [];
    const optionTitles = fromButtons.length > 0 ? fromButtons : fromList;
    if (optionTitles.length === 0) return null;
    const headerText = (typeof parsed?.header === 'string' && parsed.header.trim()) ||
      (typeof parsed?.header?.text === 'string' && parsed.header.text.trim()) || '';
    const bodyOnly = (typeof parsed?.body === 'string' && parsed.body.trim()) ||
      (typeof parsed?.body?.text === 'string' && parsed.body.text.trim()) ||
      (message.content && typeof message.content === 'string' && !message.content.trim().startsWith('item_') ? message.content.trim() : '') || '';
    const footerText = (typeof parsed?.footer === 'string' && parsed.footer.trim()) ||
      (typeof parsed?.footer?.text === 'string' && parsed.footer.text.trim()) || '';
    let bodyText = headerText && bodyOnly ? `${headerText}\n\n${bodyOnly}` : headerText || bodyOnly;
    if (footerText) bodyText = bodyText ? `${bodyText}\n\n${footerText}` : footerText;
    // Fixed message with options: extract header media (image/video/document) when present
    let headerMedia: { type: 'image' | 'video' | 'document'; url: string; filename?: string } | undefined;
    const header = parsed?.header;
    if (header && typeof header === 'object') {
      const ht = (header as any).type;
      const link = (header as any).image?.link ?? (header as any).video?.link ?? (header as any).document?.link;
      const filename = (header as any).document?.filename;
      if (link && typeof link === 'string' && (ht === 'image' || ht === 'video' || ht === 'document')) {
        headerMedia = { type: ht, url: link, filename };
      }
    }
    return { isOutbound: true, bodyText, headerText, bodyOnly, footerText, optionTitles, headerMedia };
  }, [message]);

  /** Unified reference-based special cards: product, catalog, order (and calendar). Uses referenceType + referenceId first, fallback for legacy messages. */
  const specialReferenceCard = React.useMemo((): {
    type: 'product';
    productId: string;
    routeParams?: Record<string, string>;
    bodyText?: string;
  } | {
    type: 'catalog';
    catalogId: string;
    routeParams?: Record<string, string>;
    bodyText?: string;
  } | {
    type: 'order';
    orderId: string;
    action: OrderMessageAction;
  } | null => {
    const refType = message.referenceType;
    const refId = message.referenceId;

    // --- Order: referenceType === 'order' or (no product/catalog ref) + interactiveData.orderId / externalReferences.orderId ---
    const orderId = refType === 'order' ? refId : (refType !== 'product' && refType !== 'catalog' ? (message.interactiveData?.orderId ?? message.externalReferences?.orderId) : undefined);
    // A sent invoice is a file: keep the document bubble rather than replacing it with the order card.
    if (orderId && typeof orderId === 'string' && !['document', 'image', 'video', 'audio'].includes(String(message.type))) {
      const deriveOrderAction = (): OrderMessageAction => {
        const buttonId = message.interactiveData?.buttonId;
        const extType = message.externalReferences?.type;
        const inbound = message.direction === 'inbound';
        if (inbound && typeof buttonId === 'string') {
          if (buttonId.startsWith('order_confirm_')) return 'confirmed';
          if (buttonId.startsWith('order_cancel_')) return 'cancelled';
        }
        if (extType === 'order_confirmed_invoice') return 'confirmed';
        if (extType === 'order_cancellation') return 'cancelled';
        return 'placed';
      };
      return { type: 'order', orderId, action: deriveOrderAction() };
    }

    // --- Product: referenceType === 'product' or CTA URL with productId ---
    if (refType === 'product' && refId) {
      const interactiveData = message.interactiveData || message.interactive;
      const bodyText = (typeof interactiveData?.body === 'string' ? interactiveData?.body : interactiveData?.body?.text) || message.content?.trim() || undefined;
      return { type: 'product', productId: refId, bodyText };
    }
    const interactiveData = message.interactiveData || message.interactive;
    const ctaUrl = interactiveData?.action?.parameters?.url || interactiveData?.action?.url;
    if (ctaUrl && typeof ctaUrl === 'string' && (interactiveData?.type === 'cta_url' || message.interactiveType === 'cta_url')) {
      try {
        const urlObj = new URL(ctaUrl);
        const params = Object.fromEntries(urlObj.searchParams.entries());
        const productId = params.productId || params.product_id;
        if (productId) {
          const routeParams: Record<string, string> = {};
          if (params.itemId) routeParams.itemId = params.itemId;
          if (params.businessId) routeParams.businessId = params.businessId;
          if (params.contactId) routeParams.contactId = params.contactId;
          if (params.phoneNumber) routeParams.phoneNumber = decodeURIComponent(params.phoneNumber);
          if (params.language) routeParams.language = params.language;
          const bodyText = (typeof interactiveData?.body === 'string' ? interactiveData.body : interactiveData?.body?.text) || message.content?.trim() || undefined;
          return { type: 'product', productId, routeParams, bodyText };
        }
      } catch (_) { /* ignore */ }
    }

    // --- Catalog: referenceType === 'catalog' or CTA URL with catalogId ---
    if (refType === 'catalog' && refId) {
      const interactiveData = message.interactiveData || message.interactive;
      const bodyText = (typeof interactiveData?.body === 'string' ? interactiveData?.body : interactiveData?.body?.text) || message.content?.trim() || undefined;
      return { type: 'catalog', catalogId: refId, bodyText };
    }
    if (ctaUrl && typeof ctaUrl === 'string' && (interactiveData?.type === 'cta_url' || message.interactiveType === 'cta_url')) {
      try {
        const urlObj = new URL(ctaUrl);
        const params = Object.fromEntries(urlObj.searchParams.entries());
        const catalogId = params.catalogId || params.catalog_id;
        if (catalogId) {
          const routeParams: Record<string, string> = {};
          if (params.itemId) routeParams.itemId = params.itemId;
          if (params.businessId) routeParams.businessId = params.businessId;
          if (params.contactId) routeParams.contactId = params.contactId;
          if (params.phoneNumber) routeParams.phoneNumber = decodeURIComponent(params.phoneNumber);
          if (params.language) routeParams.language = params.language;
          const bodyText = (typeof interactiveData?.body === 'string' ? interactiveData.body : interactiveData?.body?.text) || message.content?.trim() || undefined;
          return { type: 'catalog', catalogId, routeParams, bodyText };
        }
      } catch (_) { /* ignore */ }
    }

    return null;
  }, [message]);

  const [outboundBubbleWidth, setOutboundBubbleWidth] = useState<number | null>(null);
  const resolvedMaxBubbleWidth = maxBubbleWidth && maxBubbleWidth > 0 ? maxBubbleWidth : undefined;
  const outboundActionsWidth = outboundBubbleWidth ?? resolvedMaxBubbleWidth;
  const hasActionRows = !!(
    (outboundOptionsMeta && outboundOptionsMeta.optionTitles.length > 0) ||
    message.interactiveType === 'template_resolved' ||
    ctaUrlActionMeta
  );
  const messageStackWidth = hasActionRows ? outboundActionsWidth : undefined;
  React.useEffect(() => {
    if (!outboundOptionsMeta && message.interactiveType !== 'template_resolved') setOutboundBubbleWidth(null);
  }, [message.messageId, message._id, outboundOptionsMeta, message.interactiveType]);

  const isReplyMessage = !outboundOptionsMeta && !!(
    message.replyTo ||
    message.replyToMessage ||
    message.interactive?.replyTo ||
    message.interactive?.replyToMessage ||
    message.interactiveType === 'reply'
  );

  // Video player state
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoRef, setVideoRef] = useState<Video | null>(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Document viewer state
  const [isDocumentModalVisible, setIsDocumentModalVisible] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{
    filename: string;
    type: string;
    mediaUrl?: string;
    mediaType?: string;
    mediaSize?: number;
    localUri?: string;
  } | null>(null);
  // Native: per-message downloaded state (Download -> View like WhatsApp)
  const [documentLocalUri, setDocumentLocalUri] = useState<string | null>(null);
  const [documentDownloading, setDocumentDownloading] = useState(false);
  useEffect(() => {
    if (message.type !== 'document' || !message.messageId || Platform.OS === 'web') return;
    getCachedDocument(message.messageId).then((c) => setDocumentLocalUri(c?.localUri ?? null));
  }, [message.messageId, message.type]);

  const authToken = useAppStore((s) => s.tokens?.token);
  const [apiBaseURL, setApiBaseURL] = useState<string | null>(null);
  useEffect(() => {
    let mounted = true;
    getApiBaseURL().then((url) => {
      if (mounted) setApiBaseURL(url);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const toProxyMediaUrl = React.useCallback(
    (url?: string | null, mimeType?: string | null) => {
      if (!url || typeof url !== 'string') return url || '';
      const trimmed = url.trim();
      if (!trimmed) return trimmed;
      if (trimmed.startsWith('blob:')) return trimmed;

      const base = apiBaseURL?.trim();
      const isProxy = trimmed.includes('/api/storage/media/proxy');
      if (isProxy) {
        if (mimeType && !trimmed.includes('mimeType=')) {
          const join = trimmed.includes('?') ? '&' : '?';
          return `${trimmed}${join}mimeType=${encodeURIComponent(mimeType)}`;
        }
        if (trimmed.startsWith('http') || !base) return trimmed;
        return `${base.replace(/\/$/, '')}${trimmed.startsWith('/') ? '' : '/'}${trimmed}`;
      }

      const isFirebase =
        trimmed.includes('firebasestorage.googleapis.com') ||
        trimmed.includes('storage.googleapis.com');
      if (!isFirebase) return trimmed;
      if (!base) return trimmed;
      const proxyBase = `${base.replace(/\/$/, '')}/api/storage/media/proxy?url=${encodeURIComponent(trimmed)}`;
      return mimeType ? `${proxyBase}&mimeType=${encodeURIComponent(mimeType)}` : proxyBase;
    },
    [apiBaseURL]
  );

  // Context menu state
  const [isContextMenuVisible, setIsContextMenuVisible] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const bubbleRef = useRef<View | null>(null);
  const webNavigator = typeof globalThis !== 'undefined' ? (globalThis as any).navigator : undefined;
  const highlightAnim = useRef(new Animated.Value(0)).current;
  const highlightLoopRef = useRef<Animated.CompositeAnimation | null>(null);
  
  // Image viewer state
  const [isImageModalVisible, setIsImageModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Video full-screen (fixed message header video)
  const [isVideoModalVisible, setIsVideoModalVisible] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string | null>(null);

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };


  const templatePreviewText = React.useMemo(() => {
    if (messageType !== 'template') {
      return '';
    }

    const parsePayload = (value: unknown): any => {
      if (!value) {
        return null;
      }

      if (typeof value === 'string') {
        try {
          return JSON.parse(value);
        } catch (error) {
          return null;
        }
      }

      if (typeof value === 'object') {
        return value;
      }

      return null;
    };

    let payload = parsePayload(message.content);

    if (!payload && typeof message.mediaType === 'string') {
      payload = parsePayload(message.mediaType);
    }

    const extractBody = (value: any): string | undefined => {
      if (!value) {
        return undefined;
      }

      if (typeof value?.body?.text === 'string') {
        return value.body.text;
      }

      if (typeof value?.body === 'string') {
        return value.body;
      }

      if (Array.isArray(value?.components)) {
        const bodyComponent = value.components.find(
          (component: any) => (component?.type || '').toUpperCase() === 'BODY'
        );

        if (bodyComponent) {
          if (typeof bodyComponent.text === 'string') {
            return bodyComponent.text;
          }

          if (Array.isArray(bodyComponent.parameters)) {
            return bodyComponent.parameters
              .map((param: any) => {
                if (typeof param?.text === 'string') {
                  return param.text;
                }
                if (typeof param?.title === 'string') {
                  return param.title;
                }
                if (typeof param?.payload === 'string') {
                  return param.payload;
                }
                if (typeof param === 'string') {
                  return param;
                }
                return '';
              })
              .filter(Boolean)
              .join(' ')
              .trim();
          }
        }
      }

      return undefined;
    };

    const body = extractBody(payload);

    if (body && body.length > 0) {
      return body.replace(/\*(.*?)\*/g, '$1');
    }

    return 'Template message';
  }, [message, messageType]);

  const canCopyMessage = React.useMemo(() => {
    if (messageType === 'text') {
      return trimmedTextContent.length > 0;
    }

    if (messageType === 'template') {
      return templatePreviewText.length > 0;
    }

    if (messageType === 'location') {
      return !!(message.latitude && message.longitude);
    }

    if (messageType === 'contact') {
      return !!(message.contactName || message.content);
    }

    return false;
  }, [messageType, trimmedTextContent.length, templatePreviewText, message.latitude, message.longitude, message.contactName, message.content]);

  const hasSharableContent = !!(message.content || message.mediaUrl);
  const messageLocation = React.useMemo<MessageLocationPayload | null>(() => {
    if (messageType !== 'location') {
      return null;
    }

    const contentLooksLikeCoordinateLabel = (value?: string) => {
      if (!value) return false;
      return /^\s*\[?\s*(?:location\s*:\s*)?-?\d+\.?\d*\s*,\s*-?\d+\.?\d*\s*\]?\s*$/i.test(value);
    };
    const contentAddress = message.content && !contentLooksLikeCoordinateLabel(message.content)
      ? message.content
      : undefined;

    if (
      typeof message.latitude === 'number' &&
      Number.isFinite(message.latitude) &&
      typeof message.longitude === 'number' &&
      Number.isFinite(message.longitude)
    ) {
      return {
        latitude: message.latitude,
        longitude: message.longitude,
        address: message.address || contentAddress,
        text: message.text || message.address || contentAddress,
        ...(message.city && { city: message.city }),
        ...(message.country && { country: message.country }),
      };
    }

    const locationCoordMatch = message.content?.match(/(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
    if (!locationCoordMatch) {
      return null;
    }

    const latitude = parseFloat(locationCoordMatch[1]);
    const longitude = parseFloat(locationCoordMatch[2]);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return null;
    }

    return {
      latitude,
      longitude,
      address: message.address || contentAddress,
      text: message.text || message.address || contentAddress,
      ...(message.city && { city: message.city }),
      ...(message.country && { country: message.country }),
    };
  }, [messageType, message.latitude, message.longitude, message.address, message.content, message.text, message.city, message.country]);

  const openContextMenu = (event?: any) => {
    if (Platform.OS === 'web' && event?.preventDefault) {
      event.preventDefault();
    }

    const nativeEvent = event?.nativeEvent;

    if (Platform.OS === 'web' && nativeEvent?.pageX && nativeEvent?.pageY) {
      setContextMenuPosition({
        x: nativeEvent.pageX,
        y: nativeEvent.pageY,
        width: 0,
        height: 0,
      });
      setIsContextMenuVisible(true);
      return;
    }

    requestAnimationFrame(() => {
      const element = bubbleRef.current as any;
      if (element?.measureInWindow) {
        element.measureInWindow((x: number, y: number, width: number, height: number) => {
          setContextMenuPosition({ x, y, width, height });
          setIsContextMenuVisible(true);
        });
      } else {
        setIsContextMenuVisible(true);
      }
    });
  };

  const closeContextMenu = () => {
    setIsContextMenuVisible(false);
  };

  const copyMessageToClipboard = async () => {
    if (!canCopyMessage) {
      closeContextMenu();
      return;
    }

    try {
      let textToCopy = trimmedTextContent;

      if (messageType === 'template') {
        textToCopy = templatePreviewText;
      } else if (messageType === 'location') {
        textToCopy = message.latitude && message.longitude
          ? `Location: ${message.latitude}, ${message.longitude}`
          : t('chat.sharedLocation');
      } else if (messageType === 'contact') {
        textToCopy = message.contactName || message.content || t('chat.sharedContact');
      }

      if (!textToCopy) {
        closeContextMenu();
        return;
      }

      if (Platform.OS === 'web' && webNavigator?.clipboard?.writeText) {
        await webNavigator.clipboard.writeText(textToCopy);
      } else {
        await Clipboard.setStringAsync(textToCopy);
      }
      onCopy?.(message);
    } catch (error) {
      console.error('Failed to copy message:', error);
    } finally {
      closeContextMenu();
    }
  };

  const shareMessageContent = async () => {
    const shareMessage = message.content || (message.mediaUrl ? `Shared from DISLIX: ${message.mediaUrl}` : 'Shared from DISLIX');

    try {
      if (Platform.OS === 'web' && webNavigator?.share) {
        await webNavigator.share({
          title: 'DISLIX Message',
          text: message.content || undefined,
          url: message.mediaUrl || undefined,
        });
        onShare?.(message);
      } else {
        const result = await Share.share({
          message: shareMessage,
          url: message.mediaUrl,
          title: 'DISLIX Message',
        });

        if (result.action === Share.sharedAction) {
          onShare?.(message);
        }
      }
    } catch (error: any) {
      if (error?.code !== 'ECANCELLED' && error?.message !== 'User did not share') {
        console.error('Failed to share message:', error);
      }
    } finally {
      closeContextMenu();
    }
  };

  const forwardMessage = () => {
    if (messageType === 'template') {
      closeContextMenu();
      return;
    }

    if (onForward) {
      onForward(message);
    }
    closeContextMenu();
  };

  const setAsContactLocation = async () => {
    if (messageLocation && onSetContactLocation) {
      await onSetContactLocation(message, messageLocation);
    }
    closeContextMenu();
  };

  const createOrderAtLocation = async () => {
    if (messageLocation && onCreateOrderAtLocation) {
      await onCreateOrderAtLocation(message, messageLocation);
    }
    closeContextMenu();
  };

  const contextMenuOptions = [
    {
      key: 'reply',
      label: t('chat.replyLabel'),
      icon: 'arrow-undo-outline',
      onPress: () => {
        onReply?.(message);
        closeContextMenu();
      },
      disabled: !onReply,
    },
    {
      key: 'copy',
      label: t('chat.copyLabel'),
      icon: 'copy-outline',
      onPress: copyMessageToClipboard,
      disabled: !canCopyMessage,
    },
    {
      key: 'share',
      label: t('chat.shareLabel'),
      icon: 'share-outline',
      onPress: shareMessageContent,
      disabled: !hasSharableContent,
    },
    {
      key: 'forward',
      label: t('chat.forwardLabel'),
      icon: 'arrow-forward-outline',
      onPress: forwardMessage,
      disabled: !onForward || messageType === 'template',
    },
    ...(messageLocation ? [
      {
        key: 'set-contact-location',
        label: 'Set as contact location',
        icon: 'location-outline',
        onPress: setAsContactLocation,
        disabled: !onSetContactLocation,
      },
      {
        key: 'create-order-location',
        label: 'Create order here',
        icon: 'receipt-outline',
        onPress: createOrderAtLocation,
        disabled: !onCreateOrderAtLocation,
      },
    ] : []),
  ];

  const visibleContextMenuOptions = contextMenuOptions.filter((option) => !option.disabled);
  const sheetPreviewText = messageType === 'template'
    ? templatePreviewText
    : messageType === 'text'
      ? trimmedTextContent
      : '';

  const formatVideoDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent': return '✓';
      case 'delivered': return '✓✓';
      case 'read': return '✓✓';
      case 'failed': return '✗';
      case 'pending': return '🕒';
      default: return '';
    }
  };

  const showFailedDeliveryReason = () => {
    // errorCode is typed as number here but stored as a String on the backend
    // schema — compare as a string so this matches regardless of which JS
    // type actually came back over the wire.
    const isEngagementThrottle = String(message.errorCode ?? '') === '131049';
    const reason = isEngagementThrottle
      ? t('chat.deliveryThrottledExplanation')
      : message.errorMessage
      || (message.errorCode ? `${t('chat.deliveryFailedGeneric')} (${message.errorCode})` : t('chat.deliveryFailedGeneric'));
    // Alert.alert is a no-op on react-native-web (node_modules/react-native-web's Alert
    // export literally does nothing), so it silently did nothing on this app's web build —
    // use the in-app notification banner instead, which already works on both platforms.
    showNotification('error', t('chat.deliveryFailedTitle'), reason);
  };

  // Render status icon with overlapped checkmarks for delivered/read (WhatsApp style)
  const renderStatusIcon = (status: string) => {
    const statusColor = getStatusColor(status);

    if (status === 'pending') {
      return (
        <Ionicons
          name="time-outline"
          size={12}
          color={statusColor}
          style={[styles.statusIconPending, isRTL && { marginLeft: 0, marginRight: 4 }]}
        />
      );
    }

    if (status === 'delivered' || status === 'read') {
      // Render two overlapped checkmarks for delivered/read status
      return (
        <View style={[styles.doubleCheckContainer, isRTL && { marginLeft: 0, marginRight: 2 }]}>
          <Text style={[styles.statusIcon, { color: statusColor }]}>✓</Text>
          <Text style={[styles.statusIcon, styles.overlappedCheck, isRTL && { marginLeft: 0, marginRight: -6 }, { color: statusColor }]}>✓</Text>
        </View>
      );
    }

    if (status === 'failed') {
      // Tappable so the recipient-facing reason Meta gave us (e.g. "healthy ecosystem
      // engagement" throttling) is discoverable — otherwise a failed send is a dead end.
      return (
        <View style={[styles.failedStatusActions, needsFlip && { flexDirection: 'row-reverse' }]}>
          <TouchableOpacity onPress={showFailedDeliveryReason} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
            <Text style={[styles.statusIcon, { color: statusColor }]}>
              {getStatusIcon(status)}
            </Text>
          </TouchableOpacity>
          {onRetry ? (
            <TouchableOpacity
              onPress={() => onRetry(message)}
              style={styles.retryStatusButton}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              activeOpacity={0.75}
            >
              <Ionicons name="refresh" size={12} color={statusColor} />
              <Text style={[styles.retryStatusText, { color: statusColor }]}>Retry</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      );
    }

    // For other statuses, render normally
    return (
      <Text style={[styles.statusIcon, { color: statusColor }]}>
        {getStatusIcon(status)}
      </Text>
    );
  };

  const getStatusColor = (status: string) => {
    if (status === 'failed') return colors.error;
    if (isOutgoing) {
      return status === 'read' ? chat.read : chat.out.subtle;
    }
    
    // For incoming messages (though status icons typically not shown)
    switch (status) {
      case 'sent': return colors.textLight;
      case 'delivered': return colors.textLight;
      case 'read': return chat.read;
      case 'pending': return colors.warning;
      default: return colors.textLight;
    }
  };

  const handleLinkPress = (url: string) => {
    // Ensure URL has proper protocol
    let formattedUrl = url;
    
    // Convert to lowercase for protocol checking
    const lowerUrl = url.toLowerCase();
    
    if (lowerUrl.startsWith('mailto:')) {
      // Email links - keep as is
      formattedUrl = url;
    } else if (lowerUrl.startsWith('ftp://')) {
      // FTP links - keep as is
      formattedUrl = url;
    } else if (lowerUrl.startsWith('http://') || lowerUrl.startsWith('https://')) {
      // Already has protocol - keep as is (no double prefix)
      formattedUrl = url;
    } else if (lowerUrl.startsWith('www.')) {
      // www links - add https
      formattedUrl = 'https://' + url;
    } else if (/^(\d{1,3}\.){3}\d{1,3}$/.test(url)) {
      // IP addresses - add https
      formattedUrl = 'https://' + url;
    } else if (/^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/.test(url)) {
      // Domain names - add https
      formattedUrl = 'https://' + url;
    } else {
      // Fallback - add https
      formattedUrl = 'https://' + url;
    }
    
    console.log('🔗 Opening URL:', { original: url, formatted: formattedUrl, lowerUrl: lowerUrl });
    Linking.openURL(formattedUrl).catch(err => console.error('Failed to open URL:', err));
  };

  const toggleVideoPlayback = async () => {
    try {
      if (videoRef) {
        if (isVideoPlaying) {
          await videoRef.pauseAsync();
          setIsVideoPlaying(false);
        } else {
          await videoRef.playAsync();
          setIsVideoPlaying(true);
        }
      }
    } catch (error) {
      console.error('Failed to toggle video playback:', error);
    }
  };

  const openFullScreenVideo = () => {
    setIsFullScreen(true);
  };

  // Helper function to get media URL from various possible fields
  const getMediaUrl = () => {
    const interactivePayload = message.interactive && typeof message.interactive === 'object' ? message.interactive : undefined;

    return (
      message.mediaUrl ||
      interactivePayload?.mediaUrl ||
      interactivePayload?.media_url ||
      interactivePayload?.imageUrl ||
      interactivePayload?.image_url ||
      interactivePayload?.thumbnailUrl ||
      (message as any).mediaLink ||
      (message as any).url ||
      (message as any).media_url
    );
  };

  const getResolvedMediaUrl = (overrideMimeType?: string | null) =>
    toProxyMediaUrl(getMediaUrl(), overrideMimeType ?? message.mediaType);

  // Helper function to check if message has media content
  const hasMediaContent = () => {
    const mediaUrl = getMediaUrl();
    return mediaUrl && mediaUrl.length > 0;
  };

  const renderReplyPreview = () => {
    if (outboundOptionsMeta) return null;
    const replySource = message.replyToMessage as Message | undefined;
    // Only treat as interactive *reply* when type is 'reply' and has reply target (not for cta_url, product, etc.)
    const interactiveReply = message.interactiveType === 'reply' && message.interactive && typeof message.interactive === 'object' ? message.interactive : undefined;

    const hasReplyTarget = replySource || message.replyTo || (interactiveReply && (interactiveReply.replyToMessage || interactiveReply.replyTo));
    if (!hasReplyTarget) {
      return null;
    }

    const replyMeta = interactiveReply?.replyToMessage || replySource;
    const replyDirection = replyMeta?.direction || replySource?.direction;
    const resolvedSender = resolveContactLabel(
      fallbackContactName,
      replyMeta?.contactName,
      replyMeta?.from,
      interactiveReply?.sender,
      message.contactName,
      message.from
    ) || t('chat.contactLabel');

    const previewSender = replyDirection === 'outbound' || replyDirection === 'outbound-api'
      ? t('chat.you')
      : resolvedSender;

    const mediaPreviewUrl = getMediaPreviewUrl(replyMeta) || getMediaPreviewUrl(interactiveReply);
    const replyType = normaliseMessageType(replyMeta?.originalType || replyMeta?.type || interactiveReply?.originalType || interactiveReply?.type);
    const templatePreviewMeta = getTemplateMeta(replyMeta);

    const previewContentRaw = replyMeta?.content ?? interactiveReply?.body ?? interactiveReply?.text ?? interactiveReply?.description ?? interactiveReply?.title ?? '';
    let previewContent = typeof previewContentRaw === 'string'
      ? previewContentRaw.trim()
      : previewContentRaw
        ? JSON.stringify(previewContentRaw)
        : t('chat.quotedMessage');

    let templatePreviewHeader: string | undefined;

    if (templatePreviewMeta && (replyMeta?.type === 'template' || templatePreviewMeta.body || templatePreviewMeta.header)) {
      templatePreviewHeader = templatePreviewMeta.header;
      const templatePreviewBody = formatTemplatePlainText(templatePreviewMeta.body) || formatTemplatePlainText(templatePreviewMeta.footer);
      previewContent = templatePreviewBody || t('chat.templateMessage');
    }

    if (replyType === 'image' && mediaPreviewUrl) {
      previewContent = replyMeta?.mediaName || replyMeta?.mediaType || t('chat.image');
    } else if (replyType === 'video' && (previewContent === 'Quoted message' || previewContent === t('chat.quotedMessage'))) {
      previewContent = replyMeta?.mediaName || t('chat.video');
    } else if (replyType === 'document' && (previewContent === 'Quoted message' || previewContent === t('chat.quotedMessage'))) {
      previewContent = replyMeta?.mediaName || t('chat.document');
    }

    const targetId = replyMeta?.messageId || replyMeta?._id || replyMeta?.externalId || interactiveReply?.replyTo || message.replyTo;

    if (!previewContent) {
      return null;
    }

    const handlePress = () => {
      if (targetId && onScrollToMessage) {
        onScrollToMessage(String(targetId));
      }
    };

    return (
      <TouchableOpacity
        activeOpacity={0.8}
        onPress={handlePress}
        style={[styles.replyPreviewContainer, isOutgoing ? styles.replyPreviewOutgoing : styles.replyPreviewIncoming, needsFlip && { flexDirection: 'row-reverse' }]}
      >
        <View style={[styles.replyPreviewAccent, isRTL && { marginRight: 0, marginLeft: spacing.sm }]} />
        <View style={styles.replyPreviewContent}>
          <View style={[styles.replyPreviewHeader, needsFlip && { flexDirection: 'row-reverse' }]}>
            <Ionicons
              name={isRTL ? 'return-up-forward' : 'return-up-back'}
              size={14}
              color={isOutgoing ? chat.out.accent : colors.primary}
              style={styles.replyPreviewIcon}
            />
            <Text style={[styles.replyPreviewLabel, isOutgoing ? styles.replyPreviewLabelOutgoing : styles.replyPreviewLabelIncoming]}>
              {previewSender}
            </Text>
          </View>
          {templatePreviewHeader ? (
            <View style={styles.replyPreviewTemplateMetaRow}>
              <Ionicons
                name="business-outline"
                size={12}
                color={isOutgoing ? chat.out.accent : colors.primary}
                style={styles.replyPreviewTemplateIcon}
              />
              <Text
                style={[styles.replyPreviewTemplateTitle, isOutgoing ? styles.replyPreviewTemplateTitleOutgoing : styles.replyPreviewTemplateTitleIncoming]}
                numberOfLines={1}
              >
                {templatePreviewHeader}
              </Text>
            </View>
          ) : null}
          {replyType === 'image' && mediaPreviewUrl ? (
            <View style={styles.replyPreviewMediaRow}>
              {renderImageElement(mediaPreviewUrl, styles.replyPreviewMediaImage, 'reply-preview-thumbnail', (message.replyToMessage as any)?.mediaType)}
              <Text
                style={[styles.replyPreviewText, isOutgoing ? styles.replyPreviewTextOutgoing : styles.replyPreviewTextIncoming]}
                numberOfLines={2}
              >
                {previewContent}
              </Text>
            </View>
          ) : (
            <Text
              style={[styles.replyPreviewText, isOutgoing ? styles.replyPreviewTextOutgoing : styles.replyPreviewTextIncoming]}
              numberOfLines={2}
            >
              {previewContent}
            </Text>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  // Debug logging for media messages
  React.useEffect(() => {
    if ((message.type === 'image' || message.type === 'sticker') && message.messageId) {
      console.log('🔍 [MessageBubble] Media message debug:', {
        type: message.type,
        messageId: message.messageId,
        content: message.content,
        mediaUrl: message.mediaUrl,
        mediaLink: (message as any).mediaLink,
        url: (message as any).url,
        media_url: (message as any).media_url,
        hasMediaContent: hasMediaContent(),
        allKeys: Object.keys(message)
      });
    }
  }, [message]);

  const renderMessageContent = () => {
    const effectiveType = getEffectiveMessageType();
    const interactiveData = message.interactiveData || message.interactive;
    const extRefs = message.externalReferences;

    // Quick-reply rich cards: product (order), calendar (event), data_set
    const orderId = interactiveData?.orderId || extRefs?.orderId ||
      (interactiveData?.buttonId && /^order_(confirm|cancel)_/.test(interactiveData.buttonId)
        ? (interactiveData.buttonId.replace(/^order_(confirm|cancel)_/, '') || undefined)
        : undefined);
    const calendarEventId = interactiveData?.calendarEventId || extRefs?.calendarEventId ||
      (interactiveData?.buttonId && /^(calendar_|event_)/.test(String(interactiveData.buttonId))
        ? String(interactiveData.buttonId).replace(/^calendar_/, '').replace(/^event_/, '') || undefined
        : undefined);
    const isProductMessage = interactiveData?.itemType === 'product' || !!orderId ||
      (interactiveData?.buttonId && String(interactiveData.buttonId).startsWith('order_'));
    const isCalendarMessage = interactiveData?.itemType === 'calendar' || !!calendarEventId ||
      message.type === 'calendar' ||
      (interactiveData?.buttonId && /^(calendar_|event_)/.test(String(interactiveData.buttonId)));
    const isDataSetMessage = interactiveData?.itemType === 'data_set' ||
      (interactiveData?.buttonId && String(interactiveData.buttonId).startsWith('item_')) ||
      (typeof message.content === 'string' && message.content.trim().startsWith('item_'));
    const dataSetQuickReplyItemId = interactiveData?.quickReplyItemId ||
      (interactiveData?.buttonId && String(interactiveData.buttonId).startsWith('item_') ? String(interactiveData.buttonId).replace(/^item_/, '') : undefined) ||
      (typeof message.content === 'string' && message.content.trim().startsWith('item_') ? message.content.trim().replace(/^item_/, '') : undefined);

    const isInboundActionSelection = isIncoming && (isProductMessage || isCalendarMessage || isDataSetMessage);
    if (isInboundActionSelection) {
      const displayText = (message.content && typeof message.content === 'string' ? message.content.trim() : '') || 'Selected option';
      return (
        <Text style={[styles.messageText, styles.incomingText]}>
          {displayText}
        </Text>
      );
    }

    if (isProductMessage && orderId) {
      return (
        <ProductOrderCard
          orderId={orderId}
          content={message.content?.trim() || undefined}
          isOutgoing={isOutgoing}
        />
      );
    }
    if (isCalendarMessage && calendarEventId) {
      return (
        <CalendarEventCard
          calendarEventId={calendarEventId}
          content={message.content?.trim() || undefined}
          isOutgoing={isOutgoing}
        />
      );
    }
    if (isDataSetMessage) {
      return (
        <DataSetReplyCard
          content={message.content?.trim() || undefined}
          quickReplyItemId={dataSetQuickReplyItemId}
          isOutgoing={isOutgoing}
        />
      );
    }

    // CTA URL: product-view or catalog-view (system sent when contact clicked product/catalog in WhatsApp)
    const ctaUrl = interactiveData?.action?.parameters?.url || interactiveData?.action?.url;
    if (ctaUrl && typeof ctaUrl === 'string' && (interactiveData?.type === 'cta_url' || message.interactiveType === 'cta_url')) {
      try {
        const urlObj = new URL(ctaUrl);
        const params = Object.fromEntries(urlObj.searchParams.entries());
        const productId = params.productId || params.product_id;
        const catalogId = params.catalogId || params.catalog_id;
        const routeParams: Record<string, string> = {};
        if (params.itemId) routeParams.itemId = params.itemId;
        if (params.businessId) routeParams.businessId = params.businessId;
        if (params.contactId) routeParams.contactId = params.contactId;
        if (params.phoneNumber) routeParams.phoneNumber = decodeURIComponent(params.phoneNumber);
        if (params.language) routeParams.language = params.language;
        const bodyText = (typeof interactiveData?.body === 'string' ? interactiveData.body : interactiveData?.body?.text) || message.content?.trim() || undefined;
        if (productId) {
          return (
            <ProductLinkCard
              productId={productId}
              routeParams={Object.keys(routeParams).length ? routeParams : undefined}
              bodyText={bodyText}
              isOutgoing={isOutgoing}
            />
          );
        }
        if (catalogId) {
          return (
            <CatalogLinkCard
              catalogId={catalogId}
              routeParams={Object.keys(routeParams).length ? routeParams : undefined}
              bodyText={bodyText}
              isOutgoing={isOutgoing}
            />
          );
        }
      } catch (_) {}
      // Plain CTA URL fixed message: card only (header + body + footer); action follows below like OutboundOptionsList
      const headerText = (typeof interactiveData?.header === 'string' ? interactiveData.header : interactiveData?.header?.text) || '';
      const bodyText = (typeof interactiveData?.body === 'string' ? interactiveData.body : interactiveData?.body?.text) || message.content?.trim() || '';
      const footerText = (typeof interactiveData?.footer === 'string' ? interactiveData.footer : interactiveData?.footer?.text) || '';
      return (
        <View style={styles.fixedMessageContent}>
          <OutboundOptionsCard
            bodyText={bodyText}
            headerText={headerText || undefined}
            footerText={footerText || undefined}
            options={[]}
            isOutgoing={isOutgoing}
            cardOnly
          />
        </View>
      );
    }

    // Outbound interactive with options (fixed message item: type-only, body text, or multiple options)
    // Can include header media (image/video/document) + body text + options list as sibling below
    if (outboundOptionsMeta) {
      const { headerMedia } = outboundOptionsMeta;
      return (
        <View style={styles.fixedMessageContent}>
          {headerMedia ? (
            <>
              {headerMedia.type === 'image' && (
                <TouchableOpacity
                  style={styles.fixedMessageMediaWrap}
                  activeOpacity={0.9}
                  onPress={() => {
                    setSelectedImage(toProxyMediaUrl(headerMedia.url));
                    setImageLoading(true);
                    setImageError(false);
                    setIsImageModalVisible(true);
                    if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
                    loadingTimeoutRef.current = setTimeout(() => setImageLoading(false), 5000);
                  }}
                >
                  {renderImageElement(toProxyMediaUrl(headerMedia.url), styles.fixedMessageImage, 'fixed-message-image')}
                  <View style={[styles.imageOverlay, isRTL ? { right: undefined, left: 8 } : null]}>
                    <Ionicons name="expand-outline" size={20} color="white" />
                  </View>
                </TouchableOpacity>
              )}
              {headerMedia.type === 'video' && (
                <View style={styles.fixedMessageMediaWrap}>
                  <View style={styles.fixedMessageVideoInner}>
                    <Video
                      source={{ uri: toProxyMediaUrl(headerMedia.url) || '' }}
                      style={styles.fixedMessageVideo}
                      videoStyle={styles.fixedMessageVideoNative}
                      resizeMode={ResizeMode.CONTAIN}
                      useNativeControls
                      onError={(e) => console.log('Fixed message video error:', e)}
                    />
                  </View>
                  <TouchableOpacity
                    style={[styles.fixedMessageVideoExpandButton, isRTL ? { right: undefined, left: 8 } : null]}
                    onPress={() => {
                      setSelectedVideoUrl(toProxyMediaUrl(headerMedia.url));
                      setIsVideoModalVisible(true);
                    }}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  >
                    <Ionicons name="expand-outline" size={20} color="white" />
                  </TouchableOpacity>
                </View>
              )}
              {headerMedia.type === 'document' && (
                <TouchableOpacity
                  style={[styles.documentCard, isOutgoing ? styles.documentCardOutgoing : styles.documentCardIncoming]}
                  onPress={() => {
                    const name = headerMedia.filename || getDocumentDisplayName(undefined, headerMedia.url, undefined) || t('chat.document');
                    downloadDocument(headerMedia.url, name, { authToken: undefined });
                  }}
                >
                  <View style={[styles.documentIconWrap, isOutgoing ? styles.documentIconWrapOutgoing : styles.documentIconWrapIncoming]}>
                    <Ionicons name="document-text" size={24} color={isOutgoing ? chat.out.accent : colors.primary} />
                  </View>
                  <View style={styles.documentMeta}>
                    <Text style={[styles.documentFilename, isOutgoing ? styles.documentFilenameOutgoing : styles.documentFilenameIncoming]} numberOfLines={1}>
                      {headerMedia.filename || t('chat.document')}
                    </Text>
                  </View>
                  <Ionicons name="download-outline" size={20} color={isOutgoing ? chat.out.accent : colors.primary} />
                </TouchableOpacity>
              )}
            </>
          ) : null}
          <OutboundOptionsCard
            bodyText={outboundOptionsMeta.bodyOnly || outboundOptionsMeta.bodyText}
            headerText={outboundOptionsMeta.headerText}
            footerText={outboundOptionsMeta.footerText}
            options={[]}
            isOutgoing={true}
            cardOnly
          />
        </View>
      );
    }

    if (message.type === 'interactive' && (message.interactiveType === 'reply' || isReplyMessage) && effectiveType === 'interactive') {
      const replyTextSource = (typeof message.content === 'string' && message.content.trim().length > 0)
        ? message.content.trim()
        : (typeof message.interactive?.body === 'string' ? message.interactive.body : '') ||
          (typeof message.interactive?.text === 'string' ? message.interactive.text : '');

      return (
        <Text
          style={[styles.messageText, isOutgoing ? styles.outgoingText : styles.incomingText]}
        >
          {replyTextSource || t('chat.interactiveReply')}
        </Text>
      );
    }

    switch (effectiveType) {
      case 'text': {
        const trimmedContent = getDisplayContent(message.content);

        if (!trimmedContent) {
          return null;
        }

        return renderFormattedMessageText(trimmedContent, {
          baseStyle: styles.messageText,
          outgoing: isOutgoing,
          keyPrefix: `message-${message.messageId || message._id || 'text'}`,
        });
      }

      case 'image': {
        const imageCaption = getDisplayContent(message.content);
        const hasImageCaption = imageCaption && imageCaption.toLowerCase() !== 'image';
        const messageImageUrl = toProxyMediaUrl(getMediaUrl(), message.mediaType);
        return (
          <View style={styles.mediaContentWrap}>
            <View style={styles.imageContainer}>
              {hasMediaContent() ? (
                <TouchableOpacity 
                  style={styles.imageWrapper}
                  onPress={() => {
                    const imageUrl = messageImageUrl;
                    console.log('Opening image in full screen:', imageUrl);
                    
                    if (loadingTimeoutRef.current) {
                      clearTimeout(loadingTimeoutRef.current);
                    }
                    
                    setSelectedImage(imageUrl || null);
                    setImageLoading(true);
                    setImageError(false);
                    setIsImageModalVisible(true);
                    
                    loadingTimeoutRef.current = setTimeout(() => {
                      console.log('Image loading timeout, hiding loading state');
                      setImageLoading(false);
                    }, 5000);
                  }}
                  activeOpacity={0.8}
                >
                  {renderImageElement(messageImageUrl || undefined, styles.messageImage, 'message-image', message.mediaType)}
                  <View style={[styles.imageOverlay, isRTL ? { right: undefined, left: 8 } : null]}>
                    <Ionicons name="expand-outline" size={20} color="white" />
                  </View>
                </TouchableOpacity>
              ) : (
                <View style={styles.imagePlaceholder}>
                  <Ionicons name="image" size={32} color={isOutgoing ? chat.out.accent : colors.primary} />
                  <Text style={[
                    styles.placeholderText,
                    isOutgoing ? styles.outgoingText : styles.incomingText
                  ]}>
                    {t('chat.imagePlaceholder')}
                  </Text>
                </View>
              )}
            </View>
            {hasImageCaption ? (
              <View style={styles.captionWrap}>
                <Text style={[
                  styles.messageText,
                  styles.caption,
                  styles.videoCaption,
                  isOutgoing ? styles.outgoingText : styles.incomingText
                ]}>
                  {imageCaption}
                </Text>
              </View>
            ) : null}
          </View>
        );
      }

      case 'video': {
        const videoCaption = getDisplayContent(message.content);
        const hasVideoCaption = videoCaption && videoCaption.toLowerCase() !== 'video';
        const messageVideoUrl = toProxyMediaUrl(getMediaUrl(), message.mediaType);
        return (
          <View style={styles.mediaContentWrap}>
            <View style={styles.videoContainer}>
              {hasMediaContent() ? (
                <View style={styles.videoWrapper}>
                  {/* Video Player - inline playable, fits container */}
                  <Video
                    ref={(ref) => setVideoRef(ref)}
                    source={{ uri: messageVideoUrl || '' }}
                    style={styles.videoPlayer}
                    videoStyle={styles.videoMessageVideoStyle}
                    resizeMode={ResizeMode.COVER}
                    useNativeControls={false}
                    shouldPlay={isVideoPlaying}
                    isLooping={false}
                    onPlaybackStatusUpdate={(status) => {
                      if (status.isLoaded) {
                        setIsVideoPlaying(status.isPlaying);
                        if (status.durationMillis && videoDuration === 0) {
                          setVideoDuration(status.durationMillis / 1000);
                        }
                      }
                    }}
                  />
                  {/* Tap overlay on top so click plays directly */}
                  <TouchableOpacity
                    style={StyleSheet.absoluteFill}
                    onPress={toggleVideoPlayback}
                    activeOpacity={1}
                  />
                  {/* Play button - WhatsApp style: large dark triangle in circle, centered */}
                  {!isVideoPlaying && (
                    <View style={styles.videoPlayButton} pointerEvents="none">
                      <View style={styles.videoPlayButtonInner}>
                        <Ionicons name="play" size={36} color="rgba(255,255,255,0.95)" />
                      </View>
                    </View>
                  )}
                  {/* Duration - bottom-start, WhatsApp style */}
                  <View style={[styles.videoInfoOverlay, isRTL ? { left: undefined, right: 8 } : null]} pointerEvents="none">
                    <Ionicons name="videocam" size={14} color="white" />
                    <Text style={styles.videoDuration}>
                      {videoDuration > 0 ? formatVideoDuration(videoDuration) : '--:--'}
                    </Text>
                  </View>
                  {/* Expand to fullscreen */}
                  <TouchableOpacity
                    style={[styles.videoExpandButton, isRTL ? { right: undefined, left: 8 } : null]}
                    onPress={openFullScreenVideo}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  >
                    <Ionicons name="expand-outline" size={20} color="white" />
                  </TouchableOpacity>
                </View>
              ) : (
                <View style={styles.videoPlaceholder}>
                  <Ionicons name="videocam" size={32} color={isOutgoing ? chat.out.accent : colors.primary} />
                </View>
              )}
            </View>
            {hasVideoCaption ? (
              <View style={styles.captionWrap}>
                <Text style={[
                  styles.messageText,
                  styles.caption,
                  styles.videoCaption,
                  isOutgoing ? styles.outgoingText : styles.incomingText
                ]}>
                  {videoCaption}
                </Text>
              </View>
            ) : null}
          </View>
        );
      }

      case 'audio': {
        let resolvedAudioUrl = getResolvedMediaUrl('audio/mpeg');
        if (!resolvedAudioUrl) return null;
        // Defensive: this page is always served over HTTPS — never hand a
        // http:// URL to fetch()/Audio.Sound, which the browser will block
        // as mixed content (confirmed real failure: some proxied URLs carry
        // a stale http:// scheme through toProxyMediaUrl's early-return for
        // already-proxied values, bypassing the current https:// apiBaseURL).
        if (Platform.OS === 'web' && resolvedAudioUrl.startsWith('http://')) {
          resolvedAudioUrl = `https://${resolvedAudioUrl.slice('http://'.length)}`;
        }
        return <VoiceNotePlayer mediaUrl={resolvedAudioUrl} isOutgoing={isOutgoing} authToken={authToken} />;
      }

      case 'document': {
        const contentTrimmed = message.content?.trim() || '';
        const docFilename = getDocumentDisplayName(message.mediaName, getMediaUrl(), message.content);
        const bodyText = contentTrimmed && (message.mediaName || !/\.\w{2,}$/.test(contentTrimmed)) ? contentTrimmed : '';
        const docType = getDocumentType(message.mediaType, docFilename);
        const fileSizeStr = formatFileSize(message.mediaSize);
        const docMediaUrl = toProxyMediaUrl(getMediaUrl(), message.mediaType);
        const isNative = Platform.OS !== 'web';
        const isDownloaded = !!documentLocalUri;

        const openDocumentModal = (localUri?: string) => {
          setSelectedDocument({
            filename: docFilename,
            type: message.type,
            mediaUrl: docMediaUrl || undefined,
            mediaType: message.mediaType,
            mediaSize: message.mediaSize,
            localUri,
          });
          setIsDocumentModalVisible(true);
        };

        const handleDocumentPress = async () => {
          if (isNative) {
            if (isDownloaded) {
              // Open in-app viewer (modal) with local file
              openDocumentModal(documentLocalUri ?? undefined);
            } else {
              if (documentDownloading || !docMediaUrl) return;
              setDocumentDownloading(true);
              try {
                const ext = docFilename.includes('.') ? docFilename.slice(docFilename.lastIndexOf('.')) : '';
                const safeName = `${message.messageId}${ext}`;
                const localPath = `${FileSystem.documentDirectory}downloads/${safeName}`;
                const dir = `${FileSystem.documentDirectory}downloads`;
                const dirInfo = await FileSystem.getInfoAsync(dir);
                if (!dirInfo.exists) await FileSystem.makeDirectoryAsync(dir, { intermediates: true });
                const downloadOptions = authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : undefined;
                const { uri } = await FileSystem.downloadAsync(docMediaUrl, localPath, downloadOptions);
                await setCachedDocument(message.messageId, { localUri: uri, filename: docFilename });
                setDocumentLocalUri(uri);
                // Open in-app viewer directly when download finishes
                openDocumentModal(uri);
              } catch (_) {
                openDocumentModal();
              } finally {
                setDocumentDownloading(false);
              }
            }
          } else {
            openDocumentModal();
          }
        };

        return (
          <View style={styles.documentContainer}>
            <TouchableOpacity
              style={[styles.documentCard, isOutgoing ? styles.documentCardOutgoing : styles.documentCardIncoming]}
              onPress={handleDocumentPress}
              disabled={isNative && documentDownloading}
              activeOpacity={0.85}
            >
              <View style={[styles.documentIconWrap, isOutgoing ? styles.documentIconWrapOutgoing : styles.documentIconWrapIncoming]}>
                <Ionicons name={docType.icon as any} size={28} color={isOutgoing ? chat.out.accent : colors.primary} />
              </View>
              <View style={styles.documentMeta}>
                <Text style={[styles.documentFilename, isOutgoing ? styles.documentFilenameOutgoing : styles.documentFilenameIncoming]} numberOfLines={2}>
                  {docFilename}
                </Text>
                <View style={styles.documentMetaRow}>
                  <Text style={[styles.documentTypeLabel, isOutgoing ? styles.documentTypeLabelOutgoing : styles.documentTypeLabelIncoming]}>
                    {docType.label}
                  </Text>
                  {fileSizeStr ? (
                    <Text style={[styles.documentSize, isOutgoing ? styles.documentSizeOutgoing : styles.documentSizeIncoming]}>
                      {fileSizeStr}
                    </Text>
                  ) : null}
                </View>
              </View>
              <View style={[styles.documentViewBadge, isOutgoing ? styles.documentViewBadgeOutgoing : styles.documentViewBadgeIncoming]}>
                {isNative && documentDownloading ? (
                  <ActivityIndicator size="small" color={isOutgoing ? chat.out.accent : colors.primary} />
                ) : isNative && !isDownloaded ? (
                  <>
                    <Ionicons name="download-outline" size={18} color={isOutgoing ? chat.out.accent : colors.primary} />
                    <Text style={[styles.documentViewText, isOutgoing ? styles.documentViewTextOutgoing : styles.documentViewTextIncoming]}>Download</Text>
                  </>
                ) : (
                  <>
                    <Ionicons name="eye-outline" size={18} color={isOutgoing ? chat.out.accent : colors.primary} />
                    <Text style={[styles.documentViewText, isOutgoing ? styles.documentViewTextOutgoing : styles.documentViewTextIncoming]}>View</Text>
                  </>
                )}
              </View>
            </TouchableOpacity>
            {bodyText && bodyText !== docFilename ? (
              <Text style={[
                styles.messageText,
                styles.caption,
                styles.documentBodyText,
                isOutgoing ? styles.outgoingText : styles.incomingText
              ]}>
                {bodyText}
              </Text>
            ) : null}
          </View>
        );
      }

      case 'location': {
        const locationLat = messageLocation?.latitude ?? null;
        const locationLng = messageLocation?.longitude ?? null;

        const openLocation = () => {
          if (!message.content && (locationLat === null || locationLng === null)) return;
          try {
            if (message.content && (message.content.includes('maps.google.com') || message.content.includes('maps.apple.com'))) {
              Linking.openURL(message.content);
            } else if (locationLat !== null && locationLng !== null) {
              Linking.openURL(`https://maps.google.com/maps?q=${locationLat},${locationLng}`);
            } else {
              Linking.openURL(`https://maps.google.com/maps?q=${encodeURIComponent(message.content)}`);
            }
          } catch (e) {
            console.error('Failed to open location:', e);
          }
        };

        // Build OSM embed — works on all platforms, no API key needed
        // bbox: [west, south, east, north] — small box around the coordinate
        const bboxDelta = 0.004;
        const osmEmbedSrc = locationLat !== null && locationLng !== null
          ? `https://www.openstreetmap.org/export/embed.html?bbox=${locationLng - bboxDelta},${locationLat - bboxDelta * 0.7},${locationLng + bboxDelta},${locationLat + bboxDelta * 0.7}&layer=mapnik&marker=${locationLat},${locationLng}`
          : null;

        return (
          <TouchableOpacity style={styles.locationBubbleRoot} onPress={openLocation} activeOpacity={0.85}>
            {/* Map area */}
            <View style={styles.locationMapArea}>
              {osmEmbedSrc && Platform.OS === 'web' ? (
                // On web, use iframe directly (no WebView available)
                <iframe
                  src={osmEmbedSrc}
                  style={{ width: '100%', height: '100%', border: 'none', display: 'block', pointerEvents: 'none' } as any}
                  scrolling="no"
                />
              ) : osmEmbedSrc && WebView ? (
                // On native, use WebView with the OSM embed URL
                <WebView
                  source={{ uri: osmEmbedSrc }}
                  style={styles.locationMapWebView}
                  scrollEnabled={false}
                  pointerEvents="none"
                  showsHorizontalScrollIndicator={false}
                  showsVerticalScrollIndicator={false}
                />
              ) : (
                // Fallback when no coordinates or WebView unavailable
                <View style={styles.locationMapFallback}>
                  <Ionicons name="location" size={48} color="#E53935" />
                </View>
              )}
            </View>

            {/* Footer row */}
            <View style={[styles.locationFooter, needsFlip && { flexDirection: 'row-reverse' }]}>
              <Ionicons name="navigate-outline" size={14} color={isOutgoing ? chat.out.accent : colors.primary} />
              <Text style={[styles.locationFooterLabel, isOutgoing ? styles.outgoingText : styles.incomingText]} numberOfLines={1}>
                {t('chat.sharedLocation')}
              </Text>
              <View style={styles.locationFooterSpacer} />
              <Text style={[styles.timestamp, isOutgoing ? styles.outgoingTimestamp : styles.incomingTimestamp]}>
                {formatTime(message.timestamp)}
              </Text>
              {isOutgoing && renderStatusIcon(message.status)}
            </View>
          </TouchableOpacity>
        );
      }

      case 'contact':
        return (
          <View style={styles.contactContainer}>
            <View style={[styles.contactHeader, needsFlip && { flexDirection: 'row-reverse' }]}>
              <View style={[styles.contactIconBadge, isOutgoing ? styles.contactIconBadgeOutgoing : styles.contactIconBadgeIncoming]}>
                <Text style={[styles.contactInitial, isOutgoing ? styles.outgoingText : { color: colors.primary }]}>
                  {(message.contactName || '').trim().charAt(0).toUpperCase() || '?'}
                </Text>
              </View>
              <View style={styles.contactInfo}>
                <Text style={[
                  styles.contactName,
                  isOutgoing ? styles.outgoingText : styles.incomingText
                ]} numberOfLines={1}>
                  {message.contactName || t('chat.unknownContact')}
                </Text>
                <Text style={[
                  styles.contactPhone,
                  isOutgoing ? styles.outgoingText : styles.incomingText
                ]} numberOfLines={1}>
                  {message.contactPhone || message.content || t('chat.noPhoneNumber')}
                </Text>
              </View>
            </View>
            <TouchableOpacity 
              style={styles.contactAction}
              accessibilityRole="button"
              accessibilityLabel={t('chat.contactLabel')}
              onPress={() => {
                const phoneNumber = message.contactPhone || message.content;
                if (phoneNumber) {
                  Linking.openURL(`tel:${phoneNumber}`);
                }
              }}
            >
              <Ionicons 
                name="call-outline" 
                size={20} 
                color={isOutgoing ? chat.out.accent : colors.primary} 
              />
            </TouchableOpacity>
          </View>
        );

      case 'sticker':
        return (
          <View style={styles.stickerContainer}>
            {hasMediaContent() ? (
              <TouchableOpacity
                onPress={() => {
                  console.log('Sticker tapped:', getMediaUrl());
                }}
                activeOpacity={0.8}
              >
                {(() => {
                  // Check if the sticker is animated WebP
                  const mediaUrl = toProxyMediaUrl(getMediaUrl(), message.mediaType);
                  const isAnimatedWebP = mediaUrl && (
                    mediaUrl.toLowerCase().includes('.webp') || 
                    mediaUrl.toLowerCase().includes('webp')
                  );
                  
                  if (isAnimatedWebP) {
                    // For animated WebP, show with indicator but use regular Image
                    // Note: React Native Image doesn't support animated WebP
                    return (
                      <View style={styles.animatedStickerContainer}>
                        <Image
                          source={{ uri: mediaUrl || '' }}
                          style={styles.stickerImage as ImageStyle}
                          resizeMode="contain"
                          onError={() => console.log('Failed to load animated WebP sticker:', mediaUrl)}
                        />
                        <View style={[styles.animatedIndicator, isRTL ? { right: undefined, left: 8 } : null]}>
                          <Text style={styles.animatedLabel}>GIF</Text>
                        </View>
                      </View>
                    );
                  } else {
                    // Regular image stickers
                    return (
                      <Image
                          source={{ uri: mediaUrl || '' }}
                        style={styles.stickerImage as ImageStyle}
                        resizeMode="contain"
                          onError={() => console.log('Failed to load sticker:', mediaUrl)}
                      />
                    );
                  }
                })()}
              </TouchableOpacity>
            ) : (
              // Fallback for text-based stickers or emojis
              <View style={styles.stickerPlaceholder}>
                <Text style={styles.stickerEmoji}>
                  {getDisplayContent(message.content) || '🎭'}
                </Text>
              </View>
            )}
          </View>
        );

      case 'template':
        return renderTemplateMessage();

      case 'interactive':
        return renderInteractiveMessage();

      default:
        console.log('❌ [MessageBubble] Unhandled message type:', message.type, 'falling back to default');
        return (
          <Text style={[
            styles.messageText,
            isOutgoing ? styles.outgoingText : styles.incomingText
          ]}>
            {getDisplayContent(message.content)}
          </Text>
        );
    }
  };

  const renderInteractiveMessage = () => {
    const rawContent = message.content;
    let parsed: any = null;
 
    if (message.interactive) {
      parsed = message.interactive;
    } else if (rawContent && typeof rawContent === 'string') {
      try {
        parsed = JSON.parse(rawContent);
      } catch (error) {
        console.log('⚠️ [MessageBubble] Failed to parse interactive message content as JSON, using raw string.');
      }
    } else if (typeof rawContent === 'object' && rawContent !== null) {
      parsed = rawContent;
    }
 
    const bodyFromInteractive =
      (typeof parsed?.body === 'string' && parsed.body.trim()) ? parsed.body.trim() :
      (typeof parsed?.body?.text === 'string' && parsed.body.text.trim()) ? parsed.body.text.trim() :
      (typeof parsed?.text === 'string' && parsed.text.trim()) ? parsed.text.trim() :
      (typeof parsed?.title === 'string' && parsed.title.trim()) ? parsed.title.trim() :
      '';
 
    const finalContent =
      bodyFromInteractive ||
      (typeof rawContent === 'string' && rawContent.trim().length > 0 && !rawContent.trim().startsWith('{') ? rawContent.trim() : '') ||
      t('chat.interactiveReply');
 
    return (
      <View style={styles.structuredMessageCard}>
        <View style={[styles.messageTypePill, isOutgoing ? styles.messageTypePillOutgoing : styles.messageTypePillIncoming]}>
          <Ionicons name="list-outline" size={13} color={isOutgoing ? chat.out.accent : colors.primary} />
          <Text style={[styles.messageTypePillText, isOutgoing ? styles.messageTypePillTextOutgoing : styles.messageTypePillTextIncoming]}>
            Interactive
          </Text>
        </View>
        <Text style={[styles.messageText, styles.structuredMessageText, isOutgoing ? styles.outgoingText : styles.incomingText]}>
          {finalContent}
        </Text>
      </View>
    );
  };

  const renderReplySummary = () => {
    if (!message.replyToMessage) {
      return null;
    }
 
     const replied = message.replyToMessage as Message;
     const repliedFrom = replied.from === 'business' ? t('chat.you') : resolveContactLabel(
       fallbackContactName,
       replied.contactName,
       replied.from
     ) || t('chat.contactLabel');
     const templatePreviewMeta = getTemplateMeta(replied);
     const repliedPreview = templatePreviewMeta
       ? formatTemplatePlainText(templatePreviewMeta.body) || formatTemplatePlainText(templatePreviewMeta.footer) || t('chat.templateMessage')
       : getDisplayContent(replied.content) || replied.type || t('chat.previousMessage');
     const repliedHeader = templatePreviewMeta?.header;
     const repliedMediaUrl = getMediaPreviewUrl(replied);
     const repliedType = normaliseMessageType(replied.originalType || replied.type);
 
     return (
       <View style={[styles.replySummaryContainer, isOutgoing ? styles.replySummaryContainerOutgoing : styles.replySummaryContainerIncoming, isRTL && { borderLeftWidth: 0, borderRightWidth: 3, borderRightColor: isOutgoing ? chat.out.subtle : colors.primary, paddingLeft: 0, paddingRight: spacing.sm }]}>
         <View style={[styles.replySummaryHeader, needsFlip && { flexDirection: 'row-reverse' }]}>
           <Ionicons name={needsFlip ? 'return-up-forward' : 'return-up-back'} size={14} color={colors.primary} />
           <Text style={[styles.replySummaryLabel, isRTL && { marginLeft: 0, marginRight: spacing.xs / 2 }]}>{t('chat.replyingTo', { name: repliedFrom })}</Text>
         </View>
         {repliedHeader ? (
           <View style={styles.replySummaryTemplateMetaRow}>
             <Ionicons name="business-outline" size={12} color={colors.primary} style={styles.replySummaryTemplateIcon} />
             <Text style={styles.replySummaryTemplateTitle} numberOfLines={1}>
               {repliedHeader}
             </Text>
           </View>
         ) : null}
         {repliedType === 'image' && repliedMediaUrl ? (
           <View style={styles.replySummaryMediaRow}>
              {renderImageElement(repliedMediaUrl, styles.replySummaryMediaImage, 'reply-summary-thumbnail', replied.mediaType)}
             <Text style={styles.replySummaryText} numberOfLines={2}>
               {repliedPreview || replied.mediaName || t('chat.image')}
             </Text>
           </View>
         ) : (
           <Text style={styles.replySummaryText} numberOfLines={2}>
             {repliedPreview}
           </Text>
         )}
       </View>
     );
   };

  useEffect(() => {
    if (isHighlighted) {
      if (highlightLoopRef.current) {
        highlightLoopRef.current.stop();
      }
      highlightAnim.setValue(0);
      const pulse = Animated.sequence([
        Animated.timing(highlightAnim, {
          toValue: 0.7,
          duration: 220,
          useNativeDriver: true,
        }),
        Animated.timing(highlightAnim, {
          toValue: 0,
          duration: 220,
          useNativeDriver: true,
        }),
      ]);
      const loop = Animated.loop(pulse, { iterations: 2 });
      highlightLoopRef.current = loop;
      loop.start(() => {
        highlightAnim.setValue(0);
        highlightLoopRef.current = null;
      });
    } else {
      if (highlightLoopRef.current) {
        highlightLoopRef.current.stop();
        highlightLoopRef.current = null;
      }
      highlightAnim.setValue(0);
    }

    return () => {
      if (highlightLoopRef.current) {
        highlightLoopRef.current.stop();
        highlightLoopRef.current = null;
      }
      highlightAnim.stopAnimation();
    };
  }, [highlightAnim, isHighlighted]);

  const getTemplateComponents = (data: any): any[] => {
    if (!data) {
      return [];
    }

    if (Array.isArray(data.components)) {
      return data.components;
    }

    if (data.template) {
      if (Array.isArray(data.template.components)) {
        return data.template.components;
      }
      if (data.template.message && Array.isArray(data.template.message.components)) {
        return data.template.message.components;
      }
    }

    if (data.message?.template && Array.isArray(data.message.template.components)) {
      return data.message.template.components;
    }

    if (Array.isArray(data.sections)) {
      return data.sections;
    }

    return [];
  };

  const extractComponentText = (component: any): string => {
    if (!component) {
      return '';
    }

    if (typeof component.text === 'string') {
      return component.text;
    }

    if (Array.isArray(component.parameters)) {
      return component.parameters
        .map((param: any) => {
          if (!param) {
            return '';
          }
          if (typeof param.text === 'string') {
            return param.text;
          }
          if (typeof param.payload === 'string') {
            return param.payload;
          }
          if (typeof param.title === 'string') {
            return param.title;
          }
          if (param.type === 'text' && typeof param.parameter === 'string') {
            return param.parameter;
          }
          if (param.type === 'currency' && param.currency?.fallback_value) {
            return param.currency.fallback_value;
          }
          if (param.type === 'date_time' && param.date_time?.fallback_value) {
            return param.date_time.fallback_value;
          }
          if (typeof param === 'string') {
            return param;
          }
          return '';
        })
        .filter(Boolean)
        .join(' ')
        .trim();
    }

    return '';
  };

  const extractTemplateBodyText = (data: any, components: any[]): string => {
    if (!data && components.length === 0) {
      return '';
    }

    if (typeof data?.body?.text === 'string') {
      return data.body.text;
    }

    if (typeof data?.body === 'string') {
      return data.body;
    }

    const bodyComponent = components.find((component: any) => (component?.type || '').toUpperCase() === 'BODY');
    if (bodyComponent) {
      const text = extractComponentText(bodyComponent);
      if (text) {
        return text;
      }
    }

    return '';
  };

  const extractTemplateFooterText = (data: any, components: any[]): string | undefined => {
    if (typeof data?.footer?.text === 'string') {
      return data.footer.text;
    }
    if (typeof data?.footer === 'string') {
      return data.footer;
    }

    const footerComponent = components.find((component: any) => (component?.type || '').toUpperCase() === 'FOOTER');
    const text = extractComponentText(footerComponent);
    return text || undefined;
  };

  const extractTemplateHeaderText = (data: any, components: any[]): string | undefined => {
    if (typeof data?.header?.text === 'string') {
      return data.header.text;
    }
    if (typeof data?.header === 'string') {
      return data.header;
    }

    const headerComponent = components.find((component: any) => (component?.type || '').toUpperCase() === 'HEADER');
    const text = extractComponentText(headerComponent);
    return text || undefined;
  };

  const parseTemplatePayload = (source?: Message | any): any => {
    if (!source) {
      return null;
    }

    // For an actual WhatsApp template send, `interactive`/`interactiveData` (when tagged
    // interactiveType 'template_resolved') carries a denormalized snapshot of the template's
    // REAL resolved header/body/footer text (parameters substituted into the approved
    // template's sentence — see resolvedTemplateBody in sendTemplateMessageWithPersistence)
    // plus its buttons, and should be preferred. `content` (name/language/components) only
    // ever has the raw parameter VALUES, never the full sentence they fill into — it's kept
    // as a fallback for older messages sent before this snapshot existed.
    if (source.interactiveType === 'template_resolved') {
      if (source.interactive && typeof source.interactive === 'object') {
        return source.interactive;
      }
      if (source.interactiveData && typeof source.interactiveData === 'object') {
        return source.interactiveData;
      }
    }

    if (typeof source.content === 'string') {
      try {
        return JSON.parse(source.content);
      } catch (error) {
        // Ignore parse error
      }
    }

    if (source.content && typeof source.content === 'object') {
      return source.content;
    }

    return null;
  };

  const getTemplateMeta = (source?: Message | any) => {
    const payload = parseTemplatePayload(source);
    if (!payload) {
      return null;
    }

    const components = getTemplateComponents(payload);

    return {
      header: extractTemplateHeaderText(payload, components),
      body: extractTemplateBodyText(payload, components),
      footer: extractTemplateFooterText(payload, components),
    };
  };

  // Template buttons (QUICK_REPLY/URL/PHONE_NUMBER/COPY_CODE) are a snapshot of the approved
  // template's definition taken at send time — see sendTemplateMessageWithPersistence /
  // interactiveData.buttons on the backend — since Meta's send payload/response never echoes
  // button labels back, there's no other way to know what buttons a given sent message had.
  const templateButtonTitles = React.useMemo((): string[] => {
    if (messageType !== 'template' || message.interactiveType !== 'template_resolved') {
      return [];
    }
    const buttons = (message.interactiveData || message.interactive)?.buttons;
    if (!Array.isArray(buttons)) {
      return [];
    }
    return buttons.map((b: any) => b?.text).filter((t: any) => typeof t === 'string' && t.trim());
  }, [messageType, message.interactiveType, message.interactiveData, message.interactive]);

  const formatTemplatePlainText = (text?: string) => {
    if (!text) {
      return '';
    }
    return text
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/__(.*?)__/g, '$1')
      .replace(/_(.*?)_/g, '$1')
      .replace(/~(.*?)~/g, '$1')
      .replace(/```(.*?)```/g, '$1')
      .trim();
  };

  /** Header image/video/document of a sent template: snapshot first, then the raw send components, then message-level media. */
  const getTemplateHeaderMedia = (): { type: 'image' | 'video' | 'document'; url?: string; filename?: string } | null => {
    const normalize = (type: unknown, source: any) => {
      const kind = String(type || '').toLowerCase();
      if (kind !== 'image' && kind !== 'video' && kind !== 'document') return null;
      const url = typeof source === 'string' ? source : source?.url || source?.link;
      return {
        type: kind as 'image' | 'video' | 'document',
        url: typeof url === 'string' && url ? url : undefined,
        filename: typeof source?.filename === 'string' ? source.filename : undefined,
      };
    };

    const snapshot = message.interactiveData || message.interactive;
    const fromSnapshot = snapshot?.headerMedia ? normalize(snapshot.headerMedia.type, snapshot.headerMedia) : null;
    if (fromSnapshot?.url) return fromSnapshot;

    const rawContent: any = message.content;
    let payload: any = null;
    if (typeof rawContent === 'string') {
      try { payload = JSON.parse(rawContent); } catch { /* plain text content */ }
    } else if (rawContent && typeof rawContent === 'object') {
      payload = rawContent;
    }
    const headerComponent = getTemplateComponents(payload).find(
      (component: any) => String(component?.type || '').toLowerCase() === 'header'
    );
    const param = headerComponent?.parameters?.[0];
    const paramType = String(param?.type || '').toLowerCase();
    const fromContent = param ? normalize(paramType, param[paramType]) : null;
    if (fromContent?.url) return fromContent;

    if (message.mediaUrl) {
      const mime = (message.mediaType || '').toLowerCase();
      const kind = mime.startsWith('video') ? 'video' : mime.startsWith('image') ? 'image' : 'document';
      return { type: kind, url: message.mediaUrl, filename: message.mediaName };
    }

    return fromSnapshot || fromContent;
  };

  const renderTemplateHeaderMedia = () => {
    const media = getTemplateHeaderMedia();
    if (!media) return null;
    const url = media.url ? toProxyMediaUrl(media.url) : undefined;

    if (media.type === 'document') {
      const name = getDocumentDisplayName(media.filename, url, null);
      const docType = getDocumentType(undefined, name);
      return (
        <TouchableOpacity
          style={[styles.documentCard, styles.templateDocumentCard, isOutgoing ? styles.documentCardOutgoing : styles.documentCardIncoming]}
          activeOpacity={0.85}
          disabled={!url}
          onPress={() => {
            setSelectedDocument({ filename: name, type: 'document', mediaUrl: url });
            setIsDocumentModalVisible(true);
          }}
        >
          <View style={[styles.documentIconWrap, isOutgoing ? styles.documentIconWrapOutgoing : styles.documentIconWrapIncoming]}>
            <Ionicons name={docType.icon as any} size={24} color={isOutgoing ? chat.out.accent : colors.primary} />
          </View>
          <View style={styles.documentMeta}>
            <Text style={[styles.documentFilename, isOutgoing ? styles.documentFilenameOutgoing : styles.documentFilenameIncoming]} numberOfLines={2}>
              {name}
            </Text>
            <Text style={[styles.documentTypeLabel, isOutgoing ? styles.documentTypeLabelOutgoing : styles.documentTypeLabelIncoming]}>
              {docType.label}
            </Text>
          </View>
          {url ? <Ionicons name="eye-outline" size={20} color={isOutgoing ? chat.out.accent : colors.primary} /> : null}
        </TouchableOpacity>
      );
    }

    if (!url) {
      // Sent from an uploaded Meta media id: no link to show, keep the slot so the header is not lost.
      return (
        <View style={[styles.templateMediaWrap, styles.templateMediaPlaceholder]}>
          <Ionicons name={media.type === 'video' ? 'videocam-outline' : 'image-outline'} size={36} color={colors.textLight} />
          <Text style={styles.placeholderText}>{media.type === 'video' ? t('chat.video') : t('chat.image')}</Text>
        </View>
      );
    }

    if (media.type === 'image') {
      return (
        <TouchableOpacity
          style={styles.templateMediaWrap}
          activeOpacity={0.9}
          onPress={() => {
            setSelectedImage(url);
            setImageLoading(true);
            setImageError(false);
            setIsImageModalVisible(true);
            if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
            loadingTimeoutRef.current = setTimeout(() => setImageLoading(false), 5000);
          }}
        >
          {renderImageElement(url, styles.templateMediaFill, 'template-header')}
        </TouchableOpacity>
      );
    }

    return (
      <View style={styles.templateMediaWrap}>
        <Video
          source={{ uri: url }}
          style={styles.templateMediaFill}
          videoStyle={styles.fixedMessageVideoNative}
          resizeMode={ResizeMode.CONTAIN}
          useNativeControls
          onError={(e) => console.log('Template header video error:', e)}
        />
        <TouchableOpacity
          style={[styles.fixedMessageVideoExpandButton, isRTL ? { right: undefined, left: 8 } : null]}
          onPress={() => {
            setSelectedVideoUrl(url);
            setIsVideoModalVisible(true);
          }}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <Ionicons name="expand-outline" size={20} color="white" />
        </TouchableOpacity>
      </View>
    );
  };

  const renderTemplateMessage = () => {
    const templateMeta = getTemplateMeta(message);
    const headerMediaNode = renderTemplateHeaderMedia();
    const headerTitle = templateMeta?.header
      ? templateMeta.header
      : resolveContactLabel(
          businessName,
          message.contactName,
          message.from,
          fallbackContactName
        ) || fallbackContactName || businessName || message.contactName || 'Business';

    const bodyText = templateMeta?.body;
    const footerText = templateMeta?.footer;
 
    return (
      <View style={styles.templateInlineContainer}>
        {headerMediaNode}
        <View style={styles.templateTitleRow}>
          <Ionicons
            name="document-text-outline"
            size={14}
            color={isOutgoing ? chat.out.subtle : colors.primary}
          />
          <Text
            style={[
              styles.templateInlineTitle,
              isOutgoing ? styles.templateInlineTitleOutgoing : styles.templateInlineTitleIncoming
            ]}
            numberOfLines={1}
          >
            {headerTitle}
          </Text>
        </View>

        {!!bodyText && renderTemplateBodyContent(bodyText, isOutgoing)}

        {footerText ? (
          <Text
            style={[
              styles.templateInlineFooter,
              isOutgoing ? styles.templateFooterTextOutgoing : styles.templateFooterTextIncoming
            ]}
          >
            {footerText}
          </Text>
        ) : null}
      </View>
    );
  };

  const isUrl = (value?: string) => {
    if (!value) {
      return false;
    }
    return /^(https?:\/\/|www\.)/i.test(value);
  };

  const getLineDirectionStyle = (line: string) => {
    for (let index = 0; index < line.length;) {
      const codePoint = line.codePointAt(index);
      if (codePoint == null) {
        break;
      }

      if (
        (codePoint >= 0x0590 && codePoint <= 0x08FF) ||
        (codePoint >= 0xFB1D && codePoint <= 0xFDFF) ||
        (codePoint >= 0xFE70 && codePoint <= 0xFEFF)
      ) {
        return styles.formattedLineRtl;
      }

      if (
        (codePoint >= 0x0041 && codePoint <= 0x005A) ||
        (codePoint >= 0x0061 && codePoint <= 0x007A) ||
        (codePoint >= 0x00C0 && codePoint <= 0x024F)
      ) {
        return styles.formattedLineLtr;
      }

      index += codePoint > 0xFFFF ? 2 : 1;
    }

    return null;
  };

  const renderFormattedMessageText = (
    text: string,
    options: {
      baseStyle: any;
      outgoing: boolean;
      keyPrefix: string;
      preserveLineBreaks?: boolean;
    }
  ) => {
    const { baseStyle, outgoing, keyPrefix, preserveLineBreaks = true } = options;
    const tokenRegex = /(```[\s\S]*?```|\*\*[\s\S]+?\*\*|__[\s\S]+?__|\*[\s\S]+?\*|_[\s\S]+?_|~[\s\S]+?~|https?:\/\/[^\s]+|www\.[^\s]+)/g;
    const lines = preserveLineBreaks ? text.split(/\n/) : [text];
    const textColorStyle = outgoing ? styles.outgoingText : styles.incomingText;
    const linkStyle = outgoing ? styles.hyperlinkOutgoing : styles.hyperlinkIncoming;
    const shouldCollapse = text.length > 700 || lines.length > 6;
    const isCollapsed = shouldCollapse && !isLongTextExpanded;

    const renderToken = (token: string, key: string) => {
      let display = token;
      const tokenStyles: any[] = [];

      if (token.startsWith('```') && token.endsWith('```') && token.length > 6) {
        display = token.slice(3, -3);
        tokenStyles.push(styles.formattedCodeText);
      } else if (token.startsWith('**') && token.endsWith('**') && token.length > 4) {
        display = token.slice(2, -2);
        tokenStyles.push(styles.formattedBoldText);
      } else if (token.startsWith('*') && token.endsWith('*') && token.length > 2) {
        display = token.slice(1, -1);
        tokenStyles.push(styles.formattedBoldText);
      } else if (token.startsWith('__') && token.endsWith('__') && token.length > 4) {
        display = token.slice(2, -2);
        tokenStyles.push(styles.formattedUnderlineText);
      } else if (token.startsWith('_') && token.endsWith('_') && token.length > 2) {
        display = token.slice(1, -1);
        tokenStyles.push(styles.formattedItalicText);
      } else if (token.startsWith('~') && token.endsWith('~') && token.length > 2) {
        display = token.slice(1, -1);
        tokenStyles.push(styles.formattedStrikeText);
      }

      if (isUrl(display)) {
        const targetUrl = display.startsWith('http') ? display : `https://${display}`;
        return (
          <Text
            key={key}
            style={[baseStyle, styles.hyperlink, linkStyle, ...tokenStyles]}
            onPress={() => handleLinkPress(targetUrl)}
            suppressHighlighting
          >
            {display}
          </Text>
        );
      }

      return (
        <Text key={key} style={[baseStyle, textColorStyle, ...tokenStyles]}>
          {display}
        </Text>
      );
    };

    return (
      <View style={styles.formattedTextBlock}>
        <Text
          numberOfLines={isCollapsed ? 6 : undefined}
          style={[
            baseStyle,
            textColorStyle
          ]}
        >
          {lines.map((line, lineIndex) => {
            const chunks = line.split(tokenRegex);
            const lineDirectionStyle = getLineDirectionStyle(line);
            return (
              <React.Fragment key={`${keyPrefix}-line-${lineIndex}`}>
                <Text style={[baseStyle, textColorStyle, lineDirectionStyle]}>
                  {chunks.map((chunk, chunkIndex) => {
                    if (chunk === '') {
                      return null;
                    }
                    tokenRegex.lastIndex = 0;
                    if (tokenRegex.test(chunk)) {
                      tokenRegex.lastIndex = 0;
                      return renderToken(chunk, `${keyPrefix}-line-${lineIndex}-chunk-${chunkIndex}`);
                    }
                    tokenRegex.lastIndex = 0;
                    return (
                      <Text key={`${keyPrefix}-line-${lineIndex}-chunk-${chunkIndex}`} style={[baseStyle, textColorStyle]}>
                        {chunk}
                      </Text>
                    );
                  })}
                </Text>
                {lineIndex < lines.length - 1 ? '\u000A' : null}
              </React.Fragment>
            );
          })}
        </Text>
        {shouldCollapse ? (
          <TouchableOpacity
            activeOpacity={0.75}
            onPress={() => setIsLongTextExpanded((current) => !current)}
            style={styles.showMoreTextButton}
          >
            <Text style={[
              styles.showMoreText,
              outgoing ? styles.showMoreTextOutgoing : styles.showMoreTextIncoming
            ]}>
              {isLongTextExpanded ? 'Show less' : 'Show more'}
            </Text>
          </TouchableOpacity>
        ) : null}
      </View>
    );
  };

  const renderTemplateBodyContent = (text: string, outgoing: boolean) => {
    return renderFormattedMessageText(text, {
      baseStyle: styles.templateBodyText,
      outgoing,
      keyPrefix: 'template',
    });
  };

  const getMediaPreviewUrl = (source?: any): string | undefined => {
    if (!source || typeof source !== 'object') {
      return undefined;
    }

    if (typeof source.mediaUrl === 'string' && source.mediaUrl) {
      return source.mediaUrl;
    }

    if (source.media && typeof source.media === 'object') {
      if (typeof source.media.url === 'string' && source.media.url) {
        return source.media.url;
      }
      if (typeof source.media.preview_url === 'string' && source.media.preview_url) {
        return source.media.preview_url;
      }
      if (typeof source.media.link === 'string' && source.media.link) {
        return source.media.link;
      }
      if (typeof source.media.thumbnailUrl === 'string' && source.media.thumbnailUrl) {
        return source.media.thumbnailUrl;
      }
    }

    if (typeof source.imageUrl === 'string' && source.imageUrl) {
      return source.imageUrl;
    }

    if (typeof source.thumbnailUrl === 'string' && source.thumbnailUrl) {
      return source.thumbnailUrl;
    }

    if (typeof source.previewUrl === 'string' && source.previewUrl) {
      return source.previewUrl;
    }

    if (typeof source.mediaPreviewUrl === 'string' && source.mediaPreviewUrl) {
      return source.mediaPreviewUrl;
    }

    return undefined;
  };

  const normaliseMessageType = (type?: string): Message['type'] => {
    if (!type) {
      return 'text';
    }

    if (type === 'button') {
      return 'interactive';
    }

    if (type === 'location_request') {
      return 'location';
    }

    return type as Message['type'];
  };

  const getEffectiveMessageType = (): Message['type'] => {
    const interactivePayload = message.interactive && typeof message.interactive === 'object' ? message.interactive : undefined;

    if (message.type === 'interactive' && message.interactiveType === 'reply') {
      const originalType = normaliseMessageType(interactivePayload?.originalType || message.originalType);
      if (originalType && originalType !== 'interactive') {
        return originalType;
      }

      if (typeof message.mediaType === 'string') {
        if (message.mediaType.startsWith('image')) {
          return 'image';
        }
        if (message.mediaType.startsWith('video')) {
          return 'video';
        }
        if (message.mediaType.startsWith('audio')) {
          return 'audio';
        }
        if (message.mediaType.startsWith('application')) {
          return 'document';
        }
      }

      if (interactivePayload?.body === 'Image') {
        return 'image';
      }
    }

    if (message.originalType && message.originalType !== message.type && message.originalType !== 'interactive') {
      return normaliseMessageType(message.originalType);
    }

    return message.type;
  };

  const renderImageElement = (
    uri: string | undefined,
    style: any,
    contextLabel: string,
    mimeType?: string | null
  ) => {
    if (!uri) {
      return null;
    }

    const resolvedUri = toProxyMediaUrl(uri, mimeType);

    return (
      <Image
        source={{ uri: resolvedUri }}
        style={style}
        resizeMode="cover"
        onError={() => console.log(`Failed to load image (${contextLabel}):`, resolvedUri)}
      />
    );
  };

  // On web with dir="rtl", flex cross-axis is flipped: flex-start=right, flex-end=left.
  // Swap outgoing/incoming container styles so bubbles land on the correct physical side.
  const webRTLFlip = isRTL && Platform.OS === 'web';
  return (
    <View style={[
      styles.messageContainer,
      isOutgoing
        ? (webRTLFlip ? styles.incomingContainer : styles.outgoingContainer)
        : (webRTLFlip ? styles.outgoingContainer : styles.incomingContainer)
    ]}>
      <View style={[
        styles.messageStack,
        messageStackWidth ? { width: messageStackWidth, maxWidth: messageStackWidth } : undefined
      ]}>
      {/* Reference-based special cards: product, catalog, order (unified by referenceType + referenceId) */}
      {specialReferenceCard ? (
        <TouchableOpacity
          ref={bubbleRef}
          style={[
            styles.specialCardWrapper,
            resolvedMaxBubbleWidth ? { maxWidth: resolvedMaxBubbleWidth } : undefined,
            resolvedMaxBubbleWidth && isNativeMobile ? { width: resolvedMaxBubbleWidth } : undefined,
            isOutgoing
              ? (webRTLFlip ? styles.specialCardWrapperIncoming : styles.specialCardWrapperOutgoing)
              : (webRTLFlip ? styles.specialCardWrapperOutgoing : styles.specialCardWrapperIncoming)
          ]}
          activeOpacity={1}
          delayLongPress={250}
          onLongPress={openContextMenu}
          {...(Platform.OS === 'web' ? ({ onContextMenu: openContextMenu } as any) : {})}
        >
          {specialReferenceCard.type === 'product' ? (
            <ProductLinkCard
              productId={specialReferenceCard.productId}
              routeParams={specialReferenceCard.routeParams && Object.keys(specialReferenceCard.routeParams).length > 0 ? specialReferenceCard.routeParams : undefined}
              bodyText={specialReferenceCard.bodyText}
              isOutgoing={isOutgoing}
              specialCard
            />
          ) : specialReferenceCard.type === 'catalog' ? (
            <CatalogLinkCard
              catalogId={specialReferenceCard.catalogId}
              routeParams={specialReferenceCard.routeParams && Object.keys(specialReferenceCard.routeParams).length > 0 ? specialReferenceCard.routeParams : undefined}
              bodyText={specialReferenceCard.bodyText}
              isOutgoing={isOutgoing}
              specialCard
            />
          ) : specialReferenceCard.type === 'order' ? (
            <OrderMessageCard
              orderId={specialReferenceCard.orderId}
              action={specialReferenceCard.action}
              isOutgoing={isOutgoing}
              specialCard
              contactDisplayName={message.contactName}
              conversationId={conversationId}
            />
          ) : null}
          <View style={[styles.messageFooter, needsFlip && { flexDirection: 'row-reverse' }]}>
            <Text style={[styles.timestamp, isOutgoing ? styles.outgoingTimestamp : styles.incomingTimestamp]}>
              {formatTime(message.timestamp)}
            </Text>
            {isOutgoing && renderStatusIcon(message.status)}
          </View>
        </TouchableOpacity>
      ) : (
        <TouchableOpacity
          ref={bubbleRef}
          style={[
            styles.messageBubble,
            resolvedMaxBubbleWidth ? { maxWidth: resolvedMaxBubbleWidth } : undefined,
            (messageType === 'image' || messageType === 'video') && styles.mediaBubble,
            isOutgoing ? styles.outgoingBubble : styles.incomingBubble,
            // In RTL the bubble tail flips to the opposite corner
            isOutgoing && isRTL && { borderBottomRightRadius: bubbleRadius, borderBottomLeftRadius: bubbleTailRadius },
            !isOutgoing && isRTL && { borderBottomLeftRadius: bubbleRadius, borderBottomRightRadius: bubbleTailRadius },
            isLastInGroup && (isOutgoing ? styles.outgoingBubbleLast : styles.incomingBubbleLast),
            // In RTL last-in-group also flips tail back to a rounded corner on the tail side
            isLastInGroup && isOutgoing && isRTL && { borderBottomLeftRadius: bubbleTailRadius, borderBottomRightRadius: bubbleRadius },
            isLastInGroup && !isOutgoing && isRTL && { borderBottomRightRadius: bubbleTailRadius, borderBottomLeftRadius: bubbleRadius },
            (messageType === 'image' || messageType === 'video') && isLastInGroup && styles.mediaBubbleLast,
            isLinkOnlyMessage && styles.linkOnlyBubble,
            isLinkOnlyMessage && (isOutgoing ? styles.linkOnlyBubbleOutgoing : styles.linkOnlyBubbleIncoming),
            messageType === 'document' && styles.documentBubble,
            messageType === 'location' && styles.locationBubbleNoPadding,
            messageType === 'template' && [
              styles.templateBubble,
              resolvedMaxBubbleWidth ? { width: resolvedMaxBubbleWidth, maxWidth: resolvedMaxBubbleWidth } : undefined
            ],
            messageType === 'interactive' && styles.structuredBubble,
            (messageType === 'image' || messageType === 'video') && { maxWidth: mediaPreviewMaxWidth + 2 },
            ctaUrlActionMeta && isNativeMobile && { maxWidth: numericMessageLaneMaxWidth },
            isReplyMessage && !ctaUrlActionMeta && isNativeMobile && { maxWidth: numericMessageLaneMaxWidth },
            messageType === 'audio' && isNativeMobile && { minWidth: Math.min(screenWidth * 0.52, 360), maxWidth: numericMessageLaneMaxWidth },
            outboundOptionsMeta && isNativeMobile && { maxWidth: numericMessageLaneMaxWidth }
          ]}
          activeOpacity={0.8}
          delayLongPress={250}
          onLongPress={openContextMenu}
          onLayout={outboundOptionsMeta || ctaUrlActionMeta || templateButtonTitles.length > 0 ? (e) => setOutboundBubbleWidth(e.nativeEvent.layout.width) : undefined}
          {...(Platform.OS === 'web' ? ({ onContextMenu: openContextMenu } as any) : {})}
        >
          <Animated.View
            pointerEvents="none"
            style={[
              styles.highlightOverlay,
              isOutgoing ? styles.highlightOverlayOutgoing : styles.highlightOverlayIncoming,
              { opacity: highlightAnim }
            ]}
          />
          {renderReplyPreview()}
          {renderMessageContent()}
          {/* A quotation sent from (or answered in) the chat: open it in one tap. */}
          {message.referenceType === 'quotation' && !!message.referenceId && (
            <TouchableOpacity accessibilityRole="link" onPress={() => navigateTo('quotation-detail', String(message.referenceId))}
              style={{ flexDirection: isRTL ? 'row-reverse' : 'row', alignItems: 'center', gap: 4, marginTop: 6 }}>
              <Ionicons name="document-text-outline" size={14} color={isOutgoing ? chat.out.accent : (colors.primary as string)} />
              <Text style={{ fontSize: 12, fontWeight: '600', color: isOutgoing ? chat.out.accent : (colors.primary as string) }}>{locale === 'ar' ? 'فتح عرض السعر' : 'Open quotation'}</Text>
            </TouchableOpacity>
          )}
          
          {/* Location type renders its own footer with timestamp inside locationBubbleRoot */}
          {messageType !== 'location' && (
          <View style={[
            styles.messageFooter,
            (messageType === 'image' || messageType === 'video') && styles.messageFooterMedia,
            isRTL && { flexDirection: 'row-reverse' }
          ]}>
          {/* Timestamp - Same position for all message types */}
          <Text style={[
            styles.timestamp,
            isOutgoing ? styles.outgoingTimestamp : styles.incomingTimestamp
          ]}>
            {formatTime(message.timestamp)}
          </Text>
          
          {isOutgoing && renderStatusIcon(message.status)}
        </View>
          )}
      </TouchableOpacity>
      )}

      {outboundOptionsMeta && outboundOptionsMeta.optionTitles.length > 0 ? (
        <View style={[
          styles.outboundOptionsListWrapper,
          styles.outboundOptionsListWrapperStacked
        ]}>
          <OutboundOptionsList
            options={outboundOptionsMeta.optionTitles}
            isOutgoing={true}
            compactTopMargin
            width={outboundActionsWidth}
          />
        </View>
      ) : null}

      {templateButtonTitles.length > 0 ? (
        <View style={[
          styles.outboundOptionsListWrapper,
          styles.outboundOptionsListWrapperStacked
        ]}>
          <OutboundOptionsList
            options={templateButtonTitles}
            isOutgoing={isOutgoing}
            compactTopMargin
            width={outboundActionsWidth}
          />
        </View>
      ) : null}

      {ctaUrlActionMeta ? (
        <View style={[
          styles.outboundOptionsListWrapper,
          styles.outboundOptionsListWrapperStacked,
          isNativeMobile && { maxWidth: numericMessageLaneMaxWidth }
        ]}>
          <TouchableOpacity
            style={[styles.ctaUrlButton, isOutgoing ? styles.ctaUrlButtonOutgoing : styles.ctaUrlButtonIncoming]}
            onPress={() => Linking.openURL(ctaUrlActionMeta.url)}
            activeOpacity={0.8}
          >
            <Text style={[styles.ctaUrlButtonText, isOutgoing ? styles.ctaUrlButtonTextOutgoing : styles.ctaUrlButtonTextIncoming]} numberOfLines={1}>
              {ctaUrlActionMeta.displayText}
            </Text>
            <Ionicons name="open-outline" size={18} color={isOutgoing ? chat.out.accent : colors.primary} />
          </TouchableOpacity>
        </View>
      ) : null}
      </View>

      {showTimestamp && (
        <Text style={styles.fullTimestamp}>
          {new Date(message.timestamp).toLocaleString()}
        </Text>
      )}

      <Modal
        visible={isContextMenuVisible}
        transparent
        animationType="fade"
        onRequestClose={closeContextMenu}
      >
        <View style={styles.sheetRoot}>
          <TouchableWithoutFeedback onPress={closeContextMenu}>
            <View style={styles.contextMenuOverlay} />
          </TouchableWithoutFeedback>
          <View style={styles.sheet}>
            <View style={styles.sheetHandle} />
            {sheetPreviewText ? (
              <View style={[styles.sheetPreview, isOutgoing ? styles.sheetPreviewOutgoing : styles.sheetPreviewIncoming]}>
                <Text style={[styles.sheetPreviewText, isOutgoing ? styles.outgoingText : styles.incomingText]} numberOfLines={3}>
                  {sheetPreviewText}
                </Text>
              </View>
            ) : null}
            {visibleContextMenuOptions.map((option) => (
              <TouchableOpacity
                key={option.key}
                style={[styles.contextMenuOption, needsFlip && { flexDirection: 'row-reverse' }]}
                onPress={option.onPress}
                activeOpacity={0.6}
                accessibilityRole="button"
              >
                <View style={styles.contextMenuIconChip}>
                  <Ionicons name={option.icon as any} size={19} color={colors.primary} />
                </View>
                <Text style={[styles.contextMenuOptionLabel, isRTL && { textAlign: 'right' }]}>{option.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </Modal>

      {/* Full-Screen Video Modal - on web opens in middle tab (below header) */}
      <Modal
        visible={isFullScreen}
        animationType="fade"
        presentationStyle="fullScreen"
        transparent={Platform.OS === 'web'}
        onRequestClose={() => setIsFullScreen(false)}
      >
        <View style={styles.fullScreenVideoContainer}>
          <TouchableOpacity 
            style={styles.closeButton}
            onPress={() => setIsFullScreen(false)}
          >
            <Ionicons name="close" size={32} color="white" />
          </TouchableOpacity>
          <View style={styles.fullScreenVideoWrapper}>
            <Video
              source={{ uri: getResolvedMediaUrl() || '' }}
              style={styles.fullScreenVideoPlayer}
              videoStyle={styles.fullScreenVideoPlayerVideoStyle}
              resizeMode={ResizeMode.CONTAIN}
              useNativeControls={true}
              shouldPlay={true}
              isLooping={false}
              onError={(e) => console.log('Full screen video error:', e)}
            />
          </View>
        </View>
      </Modal>

      {/* Document Viewer Modal */}
      <Modal
        visible={isDocumentModalVisible}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setIsDocumentModalVisible(false)}
      >
        <View style={styles.documentModalContainer}>
          <View style={styles.documentModalHeader}>
            <TouchableOpacity
              style={styles.documentModalClose}
              onPress={() => setIsDocumentModalVisible(false)}
              hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
            >
              <Ionicons name="close" size={26} color={colors.textDark} />
            </TouchableOpacity>
            <Text style={styles.documentModalTitle} numberOfLines={1} ellipsizeMode="middle">
              {selectedDocument?.filename || getDocumentDisplayName(undefined, selectedDocument?.mediaUrl, undefined)}
            </Text>
            {selectedDocument?.mediaUrl ? (
              <TouchableOpacity
                style={styles.documentModalDownload}
                onPress={() => {
                  if (selectedDocument?.mediaUrl) {
                    downloadDocument(selectedDocument.mediaUrl, selectedDocument.filename || 'document', {
                      authToken: authToken ?? undefined,
                      onSaved: (localUri) => {
                        setSelectedDocument((prev) => (prev ? { ...prev, localUri } : null));
                      },
                    });
                  }
                }}
                hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
              >
                <Ionicons name="download-outline" size={24} color={colors.primary} />
              </TouchableOpacity>
            ) : (
              <View style={styles.documentModalDownload} />
            )}
          </View>

          <View style={styles.documentModalContent}>
            {selectedDocument && (() => {
              const viewerType = getDocumentType(selectedDocument.mediaType, selectedDocument.filename);
              const mediaUrl = selectedDocument.mediaUrl;
              const localUri = selectedDocument.localUri;
              const pdfOrImageUri = localUri || mediaUrl;

              if (viewerType.kind === 'pdf' && pdfOrImageUri) {
                // On web: embed PDF in iframe. Native: use local file in WebView if downloaded, else Google Docs viewer.
                if (Platform.OS === 'web') {
                  return (
                    <View style={styles.documentViewerWrapper}>
                      <PdfIframeWeb uri={mediaUrl!} authToken={authToken} style={styles.documentWebView} />
                    </View>
                  );
                }
                const pdfUri = localUri
                  ? localUri
                  : `https://docs.google.com/viewerng/viewer?url=${encodeURIComponent(mediaUrl!)}`;
                return (
                  <View style={styles.documentViewerWrapper}>
                    <WebView
                      source={{ uri: pdfUri }}
                      style={styles.documentWebView}
                      originWhitelist={['*', 'file://']}
                      scalesPageToFit
                      startInLoadingState={!localUri}
                      renderLoading={() => (
                        <View style={styles.documentViewerLoading}>
                          <ActivityIndicator size="large" color={colors.primary} />
                          <Text style={styles.documentViewerLoadingText}>{t('chat.pdfLoading')}</Text>
                        </View>
                      )}
                    />
                  </View>
                );
              }

              if (viewerType.kind === 'image' && pdfOrImageUri) {
                return (
                  <View style={styles.documentViewerWrapper}>
                    <Image
                      source={{ uri: pdfOrImageUri }}
                      style={styles.documentViewerImage}
                      resizeMode="contain"
                    />
                  </View>
                );
              }

              // Word / Excel / PowerPoint: open in-app via Google Docs viewer (no external open/download).
              if ((viewerType.kind === 'word' || viewerType.kind === 'excel' || viewerType.kind === 'powerpoint') && mediaUrl) {
                const officeViewerUrl = `https://docs.google.com/viewerng/viewer?url=${encodeURIComponent(mediaUrl)}`;
                if (Platform.OS === 'web') {
                  return (
                    <OfficeViewerWebFallback filename={selectedDocument.filename} style={styles.documentViewerWrapper} />
                  );
                }
                return (
                  <View style={styles.documentViewerWrapper}>
                    <WebView
                      source={{ uri: officeViewerUrl }}
                      style={styles.documentWebView}
                      originWhitelist={['*']}
                      scalesPageToFit
                      startInLoadingState
                      renderLoading={() => (
                        <View style={styles.documentViewerLoading}>
                          <ActivityIndicator size="large" color={colors.primary} />
                          <Text style={styles.documentViewerLoadingText}>Loading {viewerType.label}…</Text>
                        </View>
                      )}
                    />
                  </View>
                );
              }

              // Generic document: try Google Docs viewer in-app (proxy is publicly accessible).
              if (mediaUrl) {
                const genericViewerUrl = `https://docs.google.com/viewerng/viewer?url=${encodeURIComponent(mediaUrl)}`;
                if (Platform.OS === 'web') {
                  return (
                    <OfficeViewerWebFallback filename={selectedDocument.filename} style={styles.documentViewerWrapper} />
                  );
                }
                return (
                  <View style={styles.documentViewerWrapper}>
                    <WebView
                      source={{ uri: genericViewerUrl }}
                      style={styles.documentWebView}
                      originWhitelist={['*']}
                      scalesPageToFit
                      startInLoadingState
                      renderLoading={() => (
                        <View style={styles.documentViewerLoading}>
                          <ActivityIndicator size="large" color={colors.primary} />
                          <Text style={styles.documentViewerLoadingText}>Loading document…</Text>
                        </View>
                      )}
                    />
                  </View>
                );
              }

              return (
                <View style={styles.documentViewerFallback}>
                  <Ionicons name="document-attach-outline" size={48} color={colors.textLight} />
                  <Text style={styles.documentViewerFallbackLabel}>No preview available</Text>
                  <Text style={styles.documentViewerFallbackName} numberOfLines={2}>{selectedDocument.filename}</Text>
                </View>
              );
            })()}
          </View>
        </View>
      </Modal>

      {/* Full-Screen Image Modal */}
      <Modal
        visible={isImageModalVisible}
        animationType="fade"
        presentationStyle="fullScreen"
        onRequestClose={() => {
          // Clear timeout when modal closes
          if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
            loadingTimeoutRef.current = null;
          }
          setIsImageModalVisible(false);
          // Reset image state when modal closes
          setTimeout(() => {
            setSelectedImage(null);
            setImageLoading(false);
            setImageError(false);
          }, 300);
        }}
      >
        <View style={styles.fullScreenImageContainer}>
          <TouchableOpacity 
            style={styles.imageCloseButton}
            onPress={() => {
              // Clear timeout when modal closes
              if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
                loadingTimeoutRef.current = null;
              }
              setIsImageModalVisible(false);
              // Reset image state when modal closes
              setTimeout(() => {
                setSelectedImage(null);
                setImageLoading(false);
                setImageError(false);
              }, 300);
            }}
          >
            <Ionicons name="close" size={32} color="white" />
          </TouchableOpacity>
          
          {selectedImage && (
            <View style={styles.fullScreenImageWrapper}>
              {imageLoading && (
                <View style={styles.imageLoadingContainer}>
                  <ActivityIndicator size="large" color="white" />
                  <Text style={styles.imageLoadingText}>Loading image...</Text>
                </View>
              )}
              {imageError && (
                <View style={styles.imageErrorContainer}>
                  <Ionicons name="image-outline" size={64} color="white" />
                  <Text style={styles.imageErrorText}>Failed to load image</Text>
                  <TouchableOpacity 
                    style={styles.retryButton}
                    onPress={() => {
                      console.log('Retrying image load:', selectedImage);
                      setImageLoading(true);
                      setImageError(false);
                    }}
                  >
                    <Text style={styles.retryButtonText}>Retry</Text>
                  </TouchableOpacity>
                </View>
              )}
              <Image
                source={{ uri: selectedImage }}
                style={styles.fullScreenImage as ImageStyle}
                resizeMode="contain"
                onLoad={() => {
                  console.log('Image loaded successfully');
                  setImageLoading(false);
                }}
                onError={() => {
                  console.log('Failed to load image');
                  setImageLoading(false);
                  setImageError(true);
                }}
              />
            </View>
          )}
        </View>
      </Modal>

      {/* Full-Screen Video Modal (fixed message header video) - on web opens in middle tab */}
      <Modal
        visible={isVideoModalVisible}
        animationType="fade"
        presentationStyle="fullScreen"
        transparent={Platform.OS === 'web'}
        onRequestClose={() => {
          setSelectedVideoUrl(null);
          setIsVideoModalVisible(false);
        }}
      >
        <View style={styles.fullScreenVideoContainer}>
          <TouchableOpacity
            style={styles.imageCloseButton}
            onPress={() => {
              setSelectedVideoUrl(null);
              setIsVideoModalVisible(false);
            }}
          >
            <Ionicons name="close" size={32} color="white" />
          </TouchableOpacity>
          {selectedVideoUrl ? (
            <View style={styles.fullScreenVideoWrapper}>
              <Video
                source={{ uri: selectedVideoUrl }}
                style={styles.fullScreenVideoPlayer}
                videoStyle={styles.fullScreenVideoPlayerVideoStyle}
                resizeMode={ResizeMode.CONTAIN}
                useNativeControls
                onError={(e) => console.log('Full screen video error:', e)}
              />
            </View>
          ) : null}
        </View>
      </Modal>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  messageContainer: {
    marginVertical: 2,
    paddingHorizontal: spacing.md,
    alignSelf: 'stretch',
    width: '100%',
    maxWidth: '100%',
  },
  outgoingContainer: {
    alignItems: 'flex-end',
    alignSelf: 'stretch',
  },
  incomingContainer: {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
  },
  messageStack: {
    maxWidth: '100%',
    alignItems: 'stretch',
  },
  outboundOptionsListWrapper: {
    alignSelf: 'flex-end',
    marginTop: spacing.xs,
  },
  outboundOptionsListWrapperStacked: {
    alignSelf: 'stretch',
    width: '100%',
    maxWidth: '100%',
    marginLeft: 0,
    marginRight: 0,
  },
  outboundOptionsListFallbackWidth: {
    width: messageLaneMaxWidth,
  },
  outboundOptionsListWrapperOutgoing: {
    alignSelf: 'flex-end',
  },
  outboundOptionsListWrapperIncoming: {
    alignSelf: 'flex-start',
  },
  specialCardWrapper: {
    width: isNativeMobile ? messageLaneMaxWidth : undefined,
    maxWidth: messageLaneMaxWidth,
    minWidth: specialCardMinWidth,
  },
  specialCardWrapperOutgoing: {
    alignSelf: 'flex-end',
  },
  specialCardWrapperIncoming: {
    alignSelf: 'flex-start',
  },
  messageBubble: {
    maxWidth: messageLaneMaxWidth,
    minWidth: 72,
    paddingHorizontal: 14,
    paddingTop: 9,
    paddingBottom: 7,
    borderRadius: bubbleRadius,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
    marginBottom: 4,
    position: 'relative',
  },
  outgoingBubble: {
    backgroundColor: chat.out.bg,
    borderBottomRightRadius: bubbleTailRadius,
  },
  incomingBubble: {
    backgroundColor: chat.in.bg,
    borderBottomLeftRadius: bubbleTailRadius,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: chat.in.border,
  },
  outgoingBubbleLast: {
    borderBottomRightRadius: bubbleRadius,
  },
  incomingBubbleLast: {
    borderBottomLeftRadius: bubbleRadius,
  },
  mediaBubble: {
    width: mediaPreviewMaxWidth + 2,
    maxWidth: mediaPreviewMaxWidth + 2,
    paddingHorizontal: 0,
    paddingTop: 3,
    paddingBottom: spacing.sm,
    overflow: 'hidden',
    borderRadius: 16,
  },
  mediaContentWrap: {
    width: '100%',
    alignSelf: 'stretch',
  },
  captionWrap: {
    width: '100%',
    alignSelf: 'stretch',
    paddingHorizontal: spacing.md,
    marginTop: 2,
  },
  mediaBubbleLast: {
    borderBottomRightRadius: 16,
    borderBottomLeftRadius: 16,
  },
  messageText: {
    fontSize: 16,
    lineHeight: 20,
  },
  outgoingText: {
    color: chat.out.text,
  },
  incomingText: {
    color: colors.textDark,
  },
  messageFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: spacing.xs,
  },
  messageFooterMedia: {
    paddingHorizontal: spacing.md,
  },
  audioTimer: {
    fontSize: 11,
    fontWeight: '500',
  },
  timestamp: {
    fontSize: 11,
    marginRight: spacing.xs,
  },
  outgoingTimestamp: {
    color: chat.out.subtle,
  },
  incomingTimestamp: {
    color: colors.textLight,
  },
  statusIcon: {
    fontSize: 12,
    fontWeight: '600',
  },
  doubleCheckContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 2,
  },
  overlappedCheck: {
    marginLeft: -6, // Overlap the checkmarks like WhatsApp
  },
  statusIconPending: {
    marginLeft: 4,
  },
  failedStatusActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  retryStatusButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: 'rgba(255, 255, 255, 0.22)',
  },
  retryStatusText: {
    fontSize: 10,
    fontWeight: '700',
  },
  fullTimestamp: {
    fontSize: 11,
    color: colors.textLight,
    textAlign: 'center',
    marginTop: spacing.xs,
    marginBottom: spacing.sm,
  },
  mediaPlaceholder: {
    alignItems: 'center',
    paddingVertical: spacing.sm,
  },
  mediaIcon: {
    fontSize: 32,
    marginBottom: spacing.xs,
  },
  mediaText: {
    fontSize: 14,
    fontWeight: '500',
  },
  // Document bubble – wider than default message bubble
  documentBubble: {
    minWidth: isNativeMobile ? Math.min(screenWidth * 0.58, 320) : 280,
    maxWidth: messageLaneMaxWidth,
  },
  templateBubble: {
    width: messageLaneMaxWidth,
    maxWidth: messageLaneMaxWidth,
    minWidth: isNativeMobile ? Math.min(screenWidth * 0.62, 420) : 0,
    paddingHorizontal: 14,
    paddingTop: 14,
    paddingBottom: 8,
    overflow: 'hidden',
  },
  structuredBubble: {
    minWidth: isNativeMobile ? Math.min(screenWidth * 0.56, 360) : 300,
  },
  // Document container (file indicator + body text below)
  documentContainer: {
    alignSelf: 'stretch',
  },
  documentBodyText: {
    marginTop: spacing.xs,
  },
  // Document card (bubble)
  documentCard: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 14,
    padding: spacing.sm,
    marginVertical: spacing.xs,
    minHeight: 64,
    gap: spacing.sm,
  },
  templateDocumentCard: {
    marginTop: 0,
  },
  documentCardOutgoing: {
    backgroundColor: chat.out.chip,
  },
  documentCardIncoming: {
    backgroundColor: 'rgba(0,0,0,0.05)',
  },
  documentIconWrap: {
    width: 44,
    height: 44,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  documentIconWrapOutgoing: {
    backgroundColor: chat.out.chip,
  },
  documentIconWrapIncoming: {
    backgroundColor: 'rgba(20, 99, 165, 0.1)',
  },
  documentMeta: {
    flex: 1,
    minWidth: 0,
  },
  documentFilename: {
    fontSize: 14,
    fontFamily: typography.fontFamily.medium,
    marginBottom: 2,
  },
  documentFilenameOutgoing: {
    color: chat.out.text,
  },
  documentFilenameIncoming: {
    color: colors.textDark,
  },
  documentMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  documentTypeLabel: {
    fontSize: 12,
    fontFamily: typography.fontFamily.medium,
  },
  documentTypeLabelOutgoing: {
    color: chat.out.subtle,
  },
  documentTypeLabelIncoming: {
    color: colors.textLight,
  },
  documentSize: {
    fontSize: 12,
    opacity: 0.85,
  },
  documentSizeOutgoing: {
    color: chat.out.subtle,
  },
  documentSizeIncoming: {
    color: colors.textLight,
  },
  documentViewBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 8,
  },
  documentViewBadgeOutgoing: {
    backgroundColor: chat.out.chip,
  },
  documentViewBadgeIncoming: {
    backgroundColor: 'rgba(20, 99, 165, 0.12)',
  },
  documentViewText: {
    fontSize: 13,
    fontFamily: typography.fontFamily.medium,
  },
  documentViewTextOutgoing: {
    color: chat.out.text,
  },
  documentViewTextIncoming: {
    color: colors.primary,
  },
  // Document Modal
  documentModalContainer: {
    flex: 1,
    backgroundColor: colors.background?.primary ?? colors.white,
  },
  documentModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    paddingTop: Platform.OS === 'ios' ? spacing.lg : spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  documentModalClose: {
    padding: spacing.sm,
    minWidth: 44,
  },
  documentModalTitle: {
    flex: 1,
    fontSize: 16,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textDark,
    textAlign: 'center',
    marginHorizontal: spacing.sm,
  },
  documentModalDownload: {
    padding: spacing.sm,
    minWidth: 44,
    alignItems: 'flex-end',
  },
  documentModalContent: {
    flex: 1,
    backgroundColor: colors.background?.primary ?? '#f5f5f5',
  },
  documentViewerWrapper: {
    flex: 1,
    minHeight: 400,
  },
  documentWebView: {
    flex: 1,
    width: '100%',
    minHeight: 400,
    backgroundColor: colors.background.surface,
  },
  documentViewerLoading: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background.surface,
  },
  documentViewerLoadingText: {
    marginTop: spacing.sm,
    fontSize: 14,
    color: colors.textLight,
  },
  documentViewerImage: {
    width: '100%',
    flex: 1,
    minHeight: 400,
  },
  documentViewerFallback: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.xl,
  },
  documentViewerFallbackIconWrap: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(20, 99, 165, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
  },
  documentViewerFallbackLabel: {
    fontSize: 18,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textDark,
    marginBottom: spacing.xs,
  },
  documentViewerFallbackName: {
    fontSize: 14,
    color: colors.textLight,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  documentViewerFallbackRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  documentViewerFallbackHint: {
    fontSize: 13,
    color: colors.textLight,
    flex: 1,
  },
  documentViewerOpenButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    backgroundColor: colors.primary,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    borderRadius: 12,
  },
  documentViewerOpenButtonText: {
    fontSize: 16,
    fontFamily: typography.fontFamily.semibold,
    color: colors.white,
  },
  wordViewer: {
    alignItems: 'center',
    padding: spacing.xl,
  },
  wordViewerText: {
    fontSize: 24,
    fontWeight: '600',
    color: colors.textDark,
    marginBottom: spacing.md,
  },
  wordViewerSubtext: {
    fontSize: 16,
    color: colors.textLight,
    marginBottom: spacing.sm,
  },
  wordViewerInfo: {
    fontSize: 14,
    color: colors.textLight,
    textAlign: 'center',
  },
  excelViewer: {
    alignItems: 'center',
    padding: spacing.xl,
  },
  excelViewerText: {
    fontSize: 24,
    fontWeight: '600',
    color: colors.textDark,
    marginBottom: spacing.md,
  },
  excelViewerSubtext: {
    fontSize: 16,
    color: colors.textLight,
    marginBottom: spacing.sm,
  },
  excelViewerInfo: {
    fontSize: 14,
    color: colors.textLight,
    textAlign: 'center',
  },
  genericViewer: {
    alignItems: 'center',
    padding: spacing.xl,
  },
  genericViewerText: {
    fontSize: 24,
    fontWeight: '600',
    color: colors.textDark,
    marginBottom: spacing.md,
  },
  genericViewerSubtext: {
    fontSize: 16,
    color: colors.textLight,
    marginBottom: spacing.sm,
  },
  genericViewerInfo: {
    fontSize: 14,
    color: colors.textLight,
    textAlign: 'center',
  },
  // Contact Message Styles
  contactContainer: {
    backgroundColor: 'rgba(127,127,127,0.12)',
    borderRadius: 16,
    padding: spacing.md,
    marginVertical: spacing.xs,
    minWidth: isNativeMobile ? Math.min(screenWidth * 0.56, 300) : 280,
  },
  messageTypePill: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs / 2,
    borderRadius: 999,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    marginBottom: spacing.xs,
  },
  messageTypePillOutgoing: {
    backgroundColor: chat.out.chip,
  },
  messageTypePillIncoming: {
    backgroundColor: colors.background.secondary,
  },
  messageTypePillText: {
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  messageTypePillTextOutgoing: {
    color: chat.out.text,
  },
  messageTypePillTextIncoming: {
    color: colors.primary,
  },
  structuredMessageCard: {
    width: '100%',
  },
  structuredMessageText: {
    lineHeight: 22,
  },
  contactHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  contactIconBadge: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  contactInitial: {
    fontSize: 18,
    fontWeight: '700',
  },
  contactIconBadgeOutgoing: {
    backgroundColor: chat.out.chip,
  },
  contactIconBadgeIncoming: {
    backgroundColor: colors.background.secondary,
  },
  contactTitle: {
    fontSize: 14,
    fontWeight: '600',
  },
  contactInfo: {
    flex: 1,
    minWidth: 0,
  },
  contactName: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 2,
  },
  contactPhone: {
    fontSize: 14,
    color: colors.textLight,
  },
  contactAction: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.sm,
    backgroundColor: 'rgba(127,127,127,0.16)',
    borderRadius: 12,
  },
  caption: {
    marginTop: 0,
    fontSize: 13,
    fontStyle: 'italic',
  },
  // Image Styles
  imageContainer: {
    marginVertical: 0,
    alignSelf: 'center',
  },
  imageWrapper: {
    position: 'relative',
    borderRadius: 12,
    overflow: 'hidden',
    width: mediaPreviewMaxWidth,
    height: mediaPreviewHeight,
    maxWidth: mediaPreviewMaxWidth,
    maxHeight: mediaPreviewHeight,
  },
  messageImage: {
    width: '100%',
    height: '100%',
    borderRadius: 12,
  },
  imageOverlay: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: 16,
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  imagePlaceholder: {
    width: screenWidth * 0.75,
    height: screenWidth * 0.6,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  placeholderText: {
    fontSize: 14,
    marginTop: 8,
    fontWeight: '500',
  },

  // Fixed message with options (type + body + options)
  fixedMessageContent: {
    width: '100%',
    alignSelf: 'stretch',
  },
  fixedMessageMediaWrap: {
    marginBottom: spacing.sm,
    borderRadius: 12,
    overflow: 'hidden',
    alignSelf: 'flex-start',
    position: 'relative',
    width: mediaPreviewMaxWidth,
    maxWidth: mediaPreviewMaxWidth,
    height: mediaPreviewHeight,
    maxHeight: mediaPreviewHeight,
  },
  fixedMessageImage: {
    width: '100%',
    height: '100%',
    borderRadius: 12,
  },
  fixedMessageVideoInner: {
    width: '100%',
    height: '100%',
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: '#000',
  },
  fixedMessageVideo: {
    width: '100%',
    height: '100%',
    borderRadius: 12,
  },
  /** Applied to the actual video element (videoStyle) so content fits the container on all platforms */
  fixedMessageVideoNative: {
    width: '100%',
    height: '100%',
    ...(Platform.OS === 'web' && { objectFit: 'contain' as any }),
  },
  fixedMessageVideoExpandButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: 16,
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Video Styles - centered in bubble
  videoContainer: {
    marginVertical: 0,
    alignSelf: 'center',
    alignItems: 'center',
  },
  videoWrapper: {
    width: mediaPreviewMaxWidth,
    aspectRatio: 1,
    maxWidth: mediaPreviewMaxWidth,
    maxHeight: mediaPreviewMaxWidth,
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative',
  },
  videoThumbnail: {
    width: mediaPreviewMaxWidth,
    height: mediaPreviewHeight,
    maxWidth: mediaPreviewMaxWidth,
    maxHeight: mediaPreviewHeight,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    borderRadius: 12,
    position: 'relative',
  },
  videoPlayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    borderRadius: 12,
    transform: [{ scale: 0.92 }],
  },
  videoMessageVideoStyle: {
    width: '100%',
    height: '100%',
    ...(Platform.OS === 'web' ? { objectFit: 'cover' as any } : {}),
  },
  videoExpandButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: 16,
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  videoThumbnailImage: {
    width: '100%',
    height: '100%',
    borderRadius: 12,
  },
  videoPlayButton: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: [{ translateX: -32 }, { translateY: -32 }],
    width: 64,
    height: 64,
    alignItems: 'center',
    justifyContent: 'center',
  },
  videoPlayButtonInner: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: 4,
  },
  videoInfoOverlay: {
    position: 'absolute',
    bottom: 8,
    left: 8,
    backgroundColor: 'rgba(0,0,0,0.55)',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
    flexDirection: 'row',
    alignItems: 'center',
  },
  videoDuration: {
    color: 'white',
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 4,
  },
  videoCaption: {
    marginTop: 2,
  },
  videoTypeIndicator: {
    position: 'absolute',
    bottom: 8,
    left: 8,
    backgroundColor: 'rgba(0,0,0,0.7)',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  videoTypeText: {
    color: 'white',
    fontSize: 10,
    fontWeight: '600',
    textTransform: 'uppercase',
  },
  fullScreenVideoContainer: {
    flex: 1,
    backgroundColor: 'black',
    justifyContent: 'center',
    alignItems: 'center',
    ...(Platform.OS === 'web' && {
      position: 'fixed' as any,
      top: 180,
      left: 355,
      right: 355,
      bottom: 0,
      zIndex: 9999,
    }),
  },
  closeButton: {
    position: 'absolute',
    top: 50,
    right: 20,
    zIndex: 1000,
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: 20,
    padding: 8,
  },
  fullScreenVideo: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
  fullScreenVideoWrapper: {
    width: '100%',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fullScreenVideoPlayer: {
    width: '100%',
    height: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    alignSelf: 'center',
  },
  fullScreenVideoPlayerVideoStyle: {
    width: '100%',
    height: '100%',
    ...(Platform.OS === 'web' && { objectFit: 'contain' as any }),
  },
  fullScreenImageContainer: {
    flex: 1,
    backgroundColor: 'black',
    justifyContent: 'center',
    alignItems: 'center',
    ...(Platform.OS === 'web' && {
      // Web-specific styles to prevent refresh issues
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 9999,
    }),
  },
  imageCloseButton: {
    position: 'absolute',
    top: 50,
    right: 20,
    zIndex: 1000,
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: 20,
    padding: 8,
  },
  fullScreenImageWrapper: {
    flex: 1,
    width: '100%',
    height: '100%',
    position: 'relative',
    ...(Platform.OS === 'web' && {
      // Web-specific styles for better image display
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }),
  },
  fullScreenImage: {
    width: '100%',
    height: '100%',
    ...(Platform.OS === 'web' && {
      objectFit: 'contain', // Better image fitting for web
    }),
  },
  imageLoadingContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.8)',
    zIndex: 1,
  },
  imageLoadingText: {
    color: 'white',
    marginTop: 16,
    fontSize: 16,
  },
  imageErrorContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.8)',
    zIndex: 1,
  },
  imageErrorText: {
    color: 'white',
    marginTop: 16,
    fontSize: 16,
  },
  retryButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 20,
    marginTop: 16,
  },
  retryButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '500',
  },
  videoPlaceholder: {
    width: screenWidth * 0.7,
    height: screenWidth * 0.5,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Sticker Styles
  stickerContainer: {
    alignItems: 'center',
    paddingVertical: spacing.xs,
  },
  stickerImage: {
    width: 120,
    height: 120,
  },
  stickerPlaceholder: {
    alignItems: 'center',
    paddingVertical: spacing.sm,
  },
  stickerEmoji: {
    fontSize: 64,
  },
  animatedStickerContainer: {
    position: 'relative',
  },
  animatedIndicator: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: 8,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  animatedLabel: {
    color: 'white',
    fontSize: 10,
    fontWeight: '600',
    textTransform: 'uppercase',
  },

  templateInlineContainer: {
    width: '100%',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  templateMediaWrap: {
    // Bleeds to the bubble edges (templateBubble padding is 14, top 14)
    marginHorizontal: -14,
    marginTop: -14,
    marginBottom: spacing.xs,
    height: mediaPreviewHeight,
    backgroundColor: '#000',
    overflow: 'hidden',
  },
  templateMediaFill: {
    width: '100%',
    height: '100%',
  },
  templateMediaPlaceholder: {
    backgroundColor: 'rgba(127,127,127,0.18)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  templateTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  templateInlineHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs / 2,
    paddingBottom: spacing.xs,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  templateInlineHeaderOutgoing: {
    borderBottomColor: chat.out.line,
  },
  templateInlineHeaderIncoming: {
    borderBottomColor: colors.border,
  },
  templateInlineIcon: {
    marginTop: 1,
  },
  templateInlineTitle: {
    flex: 1,
    fontSize: 14,
    fontWeight: '700',
  },
  templateInlineTitleOutgoing: {
    color: chat.out.text,
  },
  templateInlineTitleIncoming: {
    color: colors.primary,
  },
  templateInlineBody: {
    fontSize: 13,
    lineHeight: 18,
  },
  templateInlineFooter: {
    fontSize: 12,
    lineHeight: 17,
    opacity: 0.7,
  },
  templateBodyText: {
    fontSize: 14,
    lineHeight: 20,
  },
  templateBodyTextOutgoing: {
    color: chat.out.text,
  },
  templateBodyTextIncoming: {
    color: colors.text.primary,
  },
  formattedLineRtl: {
    writingDirection: 'rtl',
    textAlign: 'right',
  },
  formattedLineLtr: {
    writingDirection: 'ltr',
    textAlign: 'left',
  },
  formattedTextBlock: {
    width: '100%',
  },
  showMoreTextButton: {
    alignSelf: 'flex-start',
    marginTop: spacing.xs,
    paddingVertical: 2,
  },
  showMoreText: {
    fontSize: 13,
    fontWeight: '700',
    textDecorationLine: 'underline',
  },
  showMoreTextOutgoing: {
    color: chat.out.subtle,
  },
  showMoreTextIncoming: {
    color: colors.primary,
  },
  formattedBoldText: {
    fontWeight: '700',
  },
  formattedUnderlineText: {
    textDecorationLine: 'underline',
  },
  formattedItalicText: {
    fontStyle: 'italic',
  },
  formattedStrikeText: {
    textDecorationLine: 'line-through',
  },
  formattedCodeText: {
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : Platform.OS === 'android' ? 'monospace' : 'monospace',
    fontSize: 13,
    backgroundColor: 'rgba(0,0,0,0.08)',
    borderRadius: 4,
    paddingHorizontal: 4,
  },
  templateBodyTextBold: {
    fontWeight: '700',
  },
  templateBodyLink: {
    color: colors.primary,
    textDecorationLine: 'underline',
  },
  templateFooterText: {
    fontSize: 12,
    opacity: 0.6,
  },
  templateFooterTextOutgoing: {
    color: chat.out.text,
  },
  templateFooterTextIncoming: {
    color: colors.text.secondary,
  },
  templateButtonRow: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  templateButton: {
    borderRadius: 8,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderWidth: 1,
    borderColor: colors.primary,
    backgroundColor: colors.primary,
  },
  templateButtonDisabled: {
    opacity: 0.35,
  },
  templateButtonLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.white,
    letterSpacing: 0.2,
    textTransform: 'uppercase',
  },
  interactiveContainer: {
    borderWidth: 1,
    borderColor: withAlpha(colors.primary, 0.25),
    borderRadius: 10,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    marginVertical: spacing.xs,
  },
  interactiveContainerOutgoing: {
    backgroundColor: chat.out.chip,
  },
  interactiveContainerIncoming: {
    backgroundColor: 'rgba(20, 99, 165, 0.07)',
  },
  interactiveHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs / 2,
  },
  interactiveLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.primary,
    marginLeft: spacing.xs / 2,
  },
  interactiveBody: {
    flex: 1,
  },
  interactiveTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: spacing.xs / 2,
  },
  outgoingTextSecondary: {
    color: chat.out.subtle,
  },
  incomingTextSecondary: {
    color: colors.text.secondary,
  },
  interactiveDescription: {
    fontSize: 13,
    marginBottom: spacing.xs / 2,
  },
  interactiveSubtitle: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.textLight,
  },
  locationBubbleNoPadding: {
    padding: 0,
    overflow: 'hidden',
  },
  locationBubbleRoot: {
    width: 240,
    overflow: 'hidden',
  },
  locationMapArea: {
    width: 240,
    height: 160,
    backgroundColor: '#d0e4d8',
    overflow: 'hidden',
    position: 'relative',
  },
  locationMapWebView: {
    width: '100%',
    height: '100%',
  },
  locationMapFallback: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#e8f0e8',
  },

  locationFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    gap: 4,
  },
  locationFooterLabel: {
    fontSize: 12,
    fontWeight: '500',
    flexShrink: 1,
  },
  locationFooterSpacer: {
    flex: 1,
  },
  // GIF Styles
  gifContainer: {
    position: 'relative',
  },
  gifIndicator: {
    position: 'absolute',
    bottom: 8,
    left: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  gifLabel: {
    color: 'white',
    fontSize: 12,
    fontWeight: '500',
  },

  // Animated Image Styles (for GIFs and animated WebP)
  animatedImageContainer: {
    position: 'relative',
    borderRadius: 12,
    overflow: 'hidden',
  },
  animatedImageIndicator: {
    position: 'absolute',
    bottom: 8,
    left: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  animatedImageLabel: {
    color: 'white',
    fontSize: 12,
    fontWeight: '500',
  },

  // Link Button Styles (for full URL messages)
  linkButton: {
    borderRadius: 12,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    marginVertical: spacing.xs,
    borderWidth: 2,
    minHeight: 50,
  },
  outgoingLinkButton: {
    backgroundColor: colors.primary,
    borderColor: chat.out.line,
  },
  incomingLinkButton: {
    backgroundColor: colors.background.surface,
    borderColor: colors.primary,
  },
  linkButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 30,
  },
  linkButtonText: {
    fontSize: 16,
    fontWeight: '700',
    flex: 1,
    textAlign: 'center',
    marginHorizontal: spacing.sm,
    lineHeight: 20,
    color: colors.textDark,
  },
  outgoingLinkButtonText: {
    color: chat.out.text,
  },
  incomingLinkButtonText: {
    color: colors.primary,
  },

  // CTA URL fixed message: single URL button below header/body/footer
  ctaUrlButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    marginTop: spacing.sm,
    borderWidth: 2,
    minHeight: 44,
    gap: spacing.xs,
  },
  ctaUrlButtonOutgoing: {
    backgroundColor: colors.primary,
    borderColor: chat.out.line,
  },
  ctaUrlButtonIncoming: {
    backgroundColor: colors.background.surface,
    borderColor: colors.primary,
  },
  ctaUrlButtonText: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    flex: 1,
    textAlign: 'center',
  },
  ctaUrlButtonTextOutgoing: {
    color: chat.out.text,
  },
  ctaUrlButtonTextIncoming: {
    color: colors.primary,
  },

  // Hyperlink style for detected URLs
  hyperlink: {
    textDecorationLine: 'underline',
    fontWeight: '600',
  },
  hyperlinkOutgoing: {
    color: chat.out.subtle,
  },
  hyperlinkIncoming: {
    color: colors.primary,
  },
  linkPreviewContainer: {
    width: '100%',
    maxWidth: 320,
    flexDirection: 'column',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 16,
    marginVertical: spacing.xs,
    backgroundColor: colors.background.surface,
  },
  linkPreviewOutgoing: {
    backgroundColor: colors.background.surface,
    borderWidth: 1,
    borderColor: withAlpha(colors.primary, 0.13),
  },
  linkPreviewIncoming: {
    backgroundColor: withAlpha(colors.primary, 0.06),
    borderWidth: 1,
    borderColor: withAlpha(colors.primary, 0.09),
  },
  linkPreviewIconWrapper: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.05)',
  },
  linkPreviewIconWrapperOutgoing: {
    backgroundColor: withAlpha(colors.primary, 0.06),
  },
  linkPreviewIconWrapperIncoming: {
    backgroundColor: 'rgba(20, 99, 165, 0.08)',
  },
  linkPreviewRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  linkPreviewBody: {
    flex: 1,
    gap: spacing.xs / 2,
  },
  linkPreviewTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 2,
  },
  linkPreviewTitleOutgoing: {
    color: chat.out.accent,
  },
  linkPreviewTitleIncoming: {
    color: colors.textDark,
  },
  linkPreviewUrl: {
    fontSize: 12,
  },
  linkPreviewUrlOutgoing: {
    color: chat.out.accent,
  },
  linkPreviewUrlIncoming: {
    color: colors.text.secondary,
  },
  linkPreviewDescription: {
    fontSize: 11,
    marginTop: 2,
  },
  linkPreviewDescriptionOutgoing: {
    color: 'rgba(20, 99, 165, 0.65)',
  },
  linkPreviewDescriptionIncoming: {
    color: colors.text.secondary,
  },
  linkPreviewImage: {
    width: '100%',
    height: 140,
    borderRadius: 12,
  },
  replyPreviewContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 10,
    padding: spacing.sm,
    marginBottom: spacing.xs,
    backgroundColor: 'rgba(20, 99, 165, 0.08)',
    minWidth: 220,
  },
  replyPreviewOutgoing: {
    backgroundColor: chat.out.chip,
  },
  replyPreviewIncoming: {
    backgroundColor: 'rgba(20, 99, 165, 0.12)',
  },
  replyPreviewAccent: {
    width: 3,
    height: '100%',
    borderRadius: 2,
    backgroundColor: colors.primary,
    marginRight: spacing.sm,
  },
  replyPreviewContent: {
    flex: 1,
  },
  replyPreviewHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
    gap: spacing.xs,
  },
  replyPreviewLabel: {
    fontSize: 12,
    fontWeight: '600',
    opacity: 0.75,
  },
  replyPreviewLabelOutgoing: {
    color: chat.out.text,
  },
  replyPreviewLabelIncoming: {
    color: colors.primary,
  },
  replyPreviewText: {
    fontSize: 13,
  },
  replyPreviewTextOutgoing: {
    color: chat.out.text,
    opacity: 0.9,
  },
  replyPreviewTextIncoming: {
    color: colors.text.primary,
  },
  replyPreviewIcon: {
    opacity: 0.75,
  },
  replySummaryContainer: {
    borderLeftWidth: 3,
    borderLeftColor: colors.primary,
    paddingLeft: spacing.sm,
    paddingVertical: spacing.xs / 2,
    marginBottom: spacing.xs,
    backgroundColor: 'rgba(20, 99, 165, 0.08)',
    borderRadius: 8,
  },
  replySummaryContainerOutgoing: {
    backgroundColor: chat.out.chip,
    borderLeftColor: chat.out.line,
  },
  replySummaryContainerIncoming: {
    backgroundColor: 'rgba(20, 99, 165, 0.08)',
  },
  replySummaryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.xs / 4,
  },
  replySummaryLabel: {
    marginLeft: spacing.xs / 2,
    fontSize: 11,
    fontWeight: '600',
    color: colors.primary,
  },
  replySummaryText: {
    fontSize: 12,
    color: colors.text.secondary,
  },
  contextMenuOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: chat.sheetScrim,
  },
  sheetRoot: {
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  sheet: {
    width: '100%',
    maxWidth: 480,
    backgroundColor: chat.sheet,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    paddingBottom: Platform.OS === 'ios' ? spacing.xl : spacing.lg,
  },
  sheetHandle: {
    alignSelf: 'center',
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.border,
    marginBottom: spacing.md,
  },
  sheetPreview: {
    borderRadius: 16,
    paddingHorizontal: 14,
    paddingVertical: 10,
    marginBottom: spacing.sm,
  },
  sheetPreviewOutgoing: {
    backgroundColor: chat.out.bg,
  },
  sheetPreviewIncoming: {
    backgroundColor: chat.in.chip,
  },
  sheetPreviewText: {
    fontSize: 14,
    lineHeight: 20,
  },
  contextMenuOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: spacing.xs,
    gap: spacing.md,
  },
  contextMenuIconChip: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: chat.out.chip,
  },
  contextMenuOptionLabel: {
    flex: 1,
    fontSize: typography.fontSize.base,
    fontWeight: '500',
    color: colors.textDark,
  },
  
  // Audio progress bar styles
  progressContainer: {
    marginTop: spacing.xs,
    alignItems: 'center',
  },
  progressBar: {
    width: '100%',
    height: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 2,
  },
  progressTime: {
    fontSize: 12,
    marginTop: spacing.xs,
    opacity: 0.8,
  },
  
  progressInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    marginTop: spacing.xs,
  },
  linkOnlyBubble: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
  },
  linkOnlyBubbleOutgoing: {
    backgroundColor: chat.out.chip,
  },
  linkOnlyBubbleIncoming: {
    backgroundColor: 'rgba(20, 99, 165, 0.08)',
  },
  interactiveReplyContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 10,
    padding: spacing.sm,
    marginBottom: spacing.xs,
    backgroundColor: 'rgba(20, 99, 165, 0.12)',
  },
  highlightOverlay: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: 18,
    backgroundColor: colors.primary,
  },
  highlightOverlayOutgoing: {
    backgroundColor: chat.out.chip,
  },
  highlightOverlayIncoming: {
    backgroundColor: 'rgba(20, 99, 165, 0.35)',
  },
  templateActionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 999,
    borderWidth: 1,
    opacity: 0.45,
  },
  templateActionRowOutgoing: {
    borderColor: chat.out.line,
  },
  templateActionRowIncoming: {
    borderColor: colors.primary,
  },
  templateActionIcon: {
    marginTop: 1,
  },
  templateActionLabel: {
    fontSize: 13,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.2,
  },
  templateActionLabelOutgoing: {
    color: chat.out.text,
  },
  templateActionLabelIncoming: {
    color: colors.primary,
  },
  replyPreviewTemplateMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs / 2,
    marginBottom: 2,
  },
  replyPreviewTemplateIcon: {
    opacity: 0.7,
  },
  replyPreviewTemplateTitle: {
    fontSize: 11,
    fontWeight: '600',
  },
  replyPreviewTemplateTitleOutgoing: {
    color: chat.out.text,
  },
  replyPreviewTemplateTitleIncoming: {
    color: colors.primary,
  },
  replySummaryTemplateMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs / 2,
    marginBottom: spacing.xs / 2,
  },
  replySummaryTemplateIcon: {
    opacity: 0.7,
  },
  replySummaryTemplateTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.primary,
  },
  replySummaryMediaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  replySummaryMediaImage: {
    width: 44,
    height: 44,
    borderRadius: 6,
    backgroundColor: 'rgba(0,0,0,0.12)',
  },
  replyPreviewMediaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  replyPreviewMediaImage: {
    width: 44,
    height: 44,
    borderRadius: 6,
    backgroundColor: 'rgba(0,0,0,0.12)',
  },
});
